'use client';

import React, { useCallback, useEffect, useState, useRef } from 'react';
import * as Sentry from "@sentry/nextjs";
import { AuthState } from '@okta/okta-auth-js';
import { useRouter } from 'next/navigation';
import * as authService from '../services/authService';
import { checkAuthState } from '../services/authService';
import NavigationBarHorizontal, { TimelineDropdownItem } from '@/components/navigation-bar-horizontal';
import Image from 'next/image';
import { Chat } from '@/components/chat';
import { Card, CardHeader, CardBody, CardFooter } from '@heroui/card';
import { Spinner } from '@heroui/spinner';
import { CaptureError, CaptureWarning, CheckBuildVersionViaApi, FetchWithVersionCheck, getClientTimeFromStr, getDateFromDateStr, extractPathAfterBulk, getMediaType, recordStat, filterSources, filterMedia, getTokenInfo, getUserEmailForHeaders, getUsername, getCaseDisplayName } from '@/shared/functions';
import { MegaphoneIcon, SpinnerGapIcon } from '@phosphor-icons/react';
import { useWindowSize, formatTime, dedupeSources, getDisplayDateAndTime, getSortedSlides, stringifyLocation, getDescriptionFromAnswerText, dedupeMediaSources, entityEventsToSlides } from '@/shared/clientFunctions';
import { TINY_VIEWPORT_WIDTH, THRESHOLD_TO_SHOW_LOCAL_TIME_SAVED, IDLE_TIMEOUT_WARNING_MS, IDLE_BUFFER_MS, REFUSAL_MESSAGES, PASS_MESSAGES, LLM_CONFIG, MODEL_ARNS_LIST, SMALL_VIEWPORT_WIDTH, ENRICHMENT_POLL_INTERVAL_MS, ENRICHMENT_POLL_TIMEOUT_MS, isEnrichmentHistoryPending } from '@/shared/constants';
import { LDMultiKindContext, useLDClient, useFlags } from 'launchdarkly-react-client-sdk';
import { AuthContext, FeaturesContext, ToastContext, StatsContext, FullScreenContext, WalkthruContext } from './contexts';
import TpToast, { ToastLevel, ToastViewport, ToastProvider, TpToastProps } from '@/components/ui/toast';
import { CSMProvider } from '@/contexts/CSMContext';
import StatusBar from '@/components/status-bar';
import TPTable from '@/components/tp-table';
import TPPresentation from '@/components/tp-presentation';
import TPCourtroom from '@/components/tp-courtroom';
import WorkProductIndex from '@/components/work-product-index';
import WorkProductNavBar from '@/components/work-product-nav-bar';
import ThreatReport from '@/components/threat-report';
import { EventFromChat } from '@/components/answer-item';
import { SourceObject, MediaObject, MediaType, TimelinePatch, View, Slide, Source, LLMConfig, FeatureFlagTypes, RemoteStorage, isWorkProductView, ThreatReportKey } from '@/shared/types';
import { omit } from 'lodash';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { AdminPanel } from './admin/AdminPanel';
import { setCookie, deleteCookie } from 'cookies-next';
import { setEmployeeTokenForHeimdall, clearEmployeeTokenForHeimdall, getEmployeeTokenForHeimdall, decodeJwtPayload } from '@/lib/heimdall-admin-token';
import { Sandbox } from '@/components/sandbox';
import { Button, Modal, ModalBody, ModalContent, ModalHeader, ModalFooter, Input } from '@heroui/react';
import Select, { SingleValue } from "react-select";
import { SelectStyles } from '@/shared/styleDefs';
import { Answer } from '../components/answer-item';
import { WalkthruItem, WalkthruState } from '@/shared/walkthru';
import { Progress } from '@heroui/progress';
import MediaDisplay from '@/components/ui/mediaDisplay';
import { useSearchParams, usePathname } from 'next/navigation';
import TitleSlideModal from '@/components/titleSlideModal';
import { ToolsDrawerProvider, ToolsDrawerChrome } from '@/components/tools-drawer';
import EventModal, { computeEnrichmentFromApiResponse } from '@/components/eventModal';
import Intercom, { hide as hideIntercom, showNewMessage, shutdown as shutdownIntercom } from '@intercom/messenger-js-sdk';
import { useIntercomTicketForm } from './useIntercomTicketForm';
dayjs.extend(utc);
dayjs.extend(timezone);
const setAuthCookies = async (authState: AuthState) => {
  if (authState && authState.idToken?.idToken && authState.accessToken?.accessToken) {
    await setCookie('accessToken', authState.accessToken.accessToken);
    await setCookie('idToken', authState.idToken.idToken);
  }
};

/** Read JWT `exp` for client-side refresh scheduling only (not cryptographically verified). */
function intercomJwtExpiryMs(token: string): number | null {
  try {
    const segment = token.split('.')[1];
    if (!segment) return null;
    let b64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4) b64 += '=';
    const json = JSON.parse(atob(b64)) as {
      exp?: number;
    };
    return typeof json.exp === 'number' ? json.exp * 1000 : null;
  } catch {
    return null;
  }
}
const INTERCOM_JWT_REFRESH_BEFORE_EXP_MS = 5 * 60 * 1000;
const INTERCOM_JWT_REFRESH_MIN_DELAY_MS = 60 * 1000;
const INTERCOM_JWT_REFRESH_FALLBACK_MS = 11 * 60 * 60 * 1000;
const Main = ({
  features
}: {
  features: {
    [key: string]: FeatureFlagTypes | null;
  };
}) => {
  const [authState, setAuthState] = useState<AuthState | null>(null);
  const [query, setQuery] = useState<string>('');
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [realAccessToken, setRealAccessToken] = useState<string | null>(null);
  const [lastAuthTime, setLastAuthTime] = useState<number>(Date.now());
  const [isReLoginAttempted, setIsReLoginAttempted] = useState(false);
  const [authStatus, setAuthStatus] = useState<authService.AuthStatus>(authService.AuthStatus.NotLoggedIn);
  const [isAuthInitialized, setIsAuthInitialized] = useState(false);
  const [caseId, setCaseId] = useState<string | undefined>(undefined);
  const [selectedSecondOption, setSelectedSecondOption] = useState<string | undefined>(undefined);
  const [selectedView, setSelectedView] = useState<View>('chat');
  const [newTimelineIsAvailable, setNewTimelineIsAvailable] = useState(false);
  const [legacyTimelineIsAvailable, setLegacyTimelineIsAvailable] = useState(false);
  const [multipleTimelinesAvailable, setMultipleTimelinesAvailable] = useState<TimelineDropdownItem[]>([]);
  const [loadingTimeline, setLoadingTimeline] = useState(false);
  const [loadingWorkProducts, setLoadingWorkProducts] = useState(false);
  const [addEvent, setAddEvent] = useState<Slide>();
  const [featureFlags, setFeatureFlags] = useState<{
    [key: string]: FeatureFlagTypes;
  }>(features);
  const [flagOverrides, setFlagOverrides] = useState<{
    [key: string]: FeatureFlagTypes;
  }>({});
  const [feedback, setFeedback] = useState<any>(null); // sentry feedback object - not exported from sentry-nextjs
  const [showFeedbackButton, setShowFeedbackButton] = useState(true);
  const [timeSaved, setTimeSaved] = useState<number | null>(null);
  const [loadingTimeSaved, setLoadingTimeSaved] = useState(false);
  const [timeSavedLocal, setTimeSavedLocal] = useState<number | null>(null);
  const [timelineCreated, setTimelineCreated] = useState(false);
  const [isEditableTimeline, setIsEditableTimeline] = useState(false);
  const [creatingTimeline, setCreatingTimeline] = useState(false);
  const [sourcesFromChat, setSourcesFromChat] = useState<SourceObject[]>([]);
  const [timelineServiceAppearsOperational, setTimelineServiceAppearsOperational] = useState(false);
  const [allSourcesFromChat, setAllSourcesFromChat] = useState<{
    allSources: SourceObject[];
    mediaSources: MediaObject[];
  } | undefined>({
    allSources: [],
    mediaSources: []
  });
  const [eventIndex, setEventIndex] = useState<number>(0);
  const [currentColumn, setCurrentColumn] = useState<string | null>(null);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [timelinePatch, setTimelinePatch] = useState<TimelinePatch | undefined>(undefined);
  const [title, setTitle] = useState<string | undefined>(undefined);
  const [description, setDescription] = useState<string | undefined>(undefined);
  const [media, setMedia] = useState<string | undefined>(undefined);
  const [isLegacy, setIsLegacy] = useState(false);
  const [currentTimelineId, setCurrentTimelineId] = useState<string | undefined>(undefined);
  const [allSlides, setAllSlides] = useState<Slide[] | undefined>(undefined);
  const [timeline, setTimeline] = useState<any>(undefined);
  const [sortByDateTime, setSortByDateTime] = useState(false);
  const [pleaseSelectTimeline, setPleaseSelectTimeline] = useState<boolean>(false);
  const [modalSelectedTimeline, setModalSelectedTimeline] = useState<string | undefined>(undefined);
  const [docIdMode, setDocIdMode] = useState(false);
  const [chatEventToAdd, setChatEventToAdd] = useState<EventFromChat | undefined>(undefined);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullscreenMediaItem, setFullscreenMediaItem] = useState<MediaObject | null>(null);
  const [linkedMediaItem, setLinkedMediaItem] = useState<MediaObject | null>(null);
  const [captureData, setCaptureData] = useState<string | undefined>(undefined);
  const [timelineTags, setTimelineTags] = useState<string[]>([]);
  const [allWalkthrus, setAllWalkthrus] = useState<Record<string, WalkthruState>>(localStorage.getItem('walkthrus') ? JSON.parse(localStorage.getItem('walkthrus') || '') : []);
  const [parsedEventsFromChat, setParsedEventsFromChat] = useState<Slide[] | undefined>(undefined);
  const [modelArnsList, setModelArnsList] = useState<any>(null);
  const [llmConfig, setLlmConfig] = useState<LLMConfig>(LLM_CONFIG);
  const [refusalMessages, setRefusalMessages] = useState<string[]>(REFUSAL_MESSAGES);
  const [passMessages, setPassMessages] = useState<string[]>(PASS_MESSAGES);
  const [useEnrichment, setUseEnrichment] = useState<boolean>(false);
  const [entityServiceJob, setEntityServiceJob] = useState<{
    jobId: string | undefined;
    caseId: string | undefined;
    count: number;
  }>({
    jobId: undefined,
    caseId: undefined,
    count: 0
  });
  const [entityExtractionJobs, setEntityExtractionJobs] = useState<any[]>([]);
  const [entityExtractionJobsWithQuestions, setEntityExtractionJobsWithQuestions] = useState<any[]>([]);
  const [chatSessionId, setChatSessionId] = useState<string | null>(null);
  const [chatHistorySessions, setChatHistorySessions] = useState<any[] | null>(null);
  const [newAnswer, setNewAnswer] = useState<Answer | null>(null);
  const [entityJobId, setEntityJobId] = useState<string | undefined>(undefined);
  const [jobsBeingDeleted, setJobsBeingDeleted] = useState<string[]>([]);
  const [isCognitoUser, setIsCognitoUser] = useState(false);
  const [cognitoClientId, setCognitoClientId] = useState<string | undefined>(undefined);
  const [tenantName, setTenantName] = useState<string | undefined | null>(undefined);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [orgName, setOrgName] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isTimelineNameModalOpen, setIsTimelineNameModalOpen] = useState<boolean>(false);
  const [timelineName, setTimelineName] = useState<string>('');
  const [pendingTimelineCreation, setPendingTimelineCreation] = useState<{
    creatingEventFromChat?: boolean;
  } | null>(null);
  const [isThreatReportNameModalOpen, setIsThreatReportNameModalOpen] = useState<boolean>(false);
  const [threatReportName, setThreatReportName] = useState<string>('');
  const [isThreatReportProcessing, setIsThreatReportProcessing] = useState<boolean>(false);
  const [newThreatReportId, setNewThreatReportId] = useState<string | null>(null);
  const [threatReportKey, setThreatReportKey] = useState<ThreatReportKey | undefined>(undefined);
  const [isUserAdmin, setIsUserAdmin] = useState<boolean>(false);
  const [isUserServiceAvailable, setIsUserServiceAvailable] = useState<boolean>(false);
  const [entityServicePollAllJobsCount, setEntityServicePollAllJobsCount] = useState<number>(0);
  const [remoteStorageLink, setRemoteStorageLink] = useState<RemoteStorage | null>(null);
  const [remoteStorageLinkLocal, setRemoteStorageLinkLocal] = useState<RemoteStorage | null>(null);
  const [lastWorkProductView, setLastWorkProductView] = useState<View | null>(null);
  const [lastWorkProductTimelineId, setLastWorkProductTimelineId] = useState<string | undefined>(undefined);
  const [lastWorkProductCaseId, setLastWorkProductCaseId] = useState<string | undefined>(undefined);
  const [workProductViewMode, setWorkProductViewMode] = useState<'grid' | 'list'>('grid');
  const [threatReportJson, setThreatReportJson] = useState<any>(null);
  const [threatReportSources, setThreatReportSources] = useState<any>({
    fiveCastFiles: [],
    magnetFiles: []
  });
  const [selectedFivecastSource, setSelectedFivecastSource] = useState<string | undefined>(undefined);
  const [selectedMagnetSource, setSelectedMagnetSource] = useState<string | undefined>(undefined);
  const [isBannerModalOpen, setIsBannerModalOpen] = useState<boolean>(false);
  const [banners, setBanners] = useState<string[] | undefined>(undefined);
  const [isTitleSlideModalOpen, setIsTitleSlideModalOpen] = useState<boolean>(false);
  const [intercomJwt, setIntercomJwt] = useState<string | null>(null);
  const intercomAuthHeadersRef = useRef({
    access: '',
    idToken: ''
  });
  intercomAuthHeadersRef.current = {
    access: authState?.accessToken?.accessToken ?? '',
    idToken: authState?.idToken?.idToken ?? ''
  };
  const bannerModalAcceptedRef = useRef<boolean>(false);
  const entityServiceJobPollRef = useRef<NodeJS.Timeout | null>(null);
  const entityServicePollAllJobsRef = useRef<NodeJS.Timeout | null>(null);
  const windowSize = useWindowSize();
  const isSmallViewport = windowSize.width && windowSize.width < SMALL_VIEWPORT_WIDTH;
  const logoWidth = isSmallViewport ? 300 : 733;
  const logoHeight = isSmallViewport ? 300 * 233.7 / 733 : 233.7; // Maintain aspect ratio

  const lastActivityTimeRef = useRef<number>(Date.now());
  const feedbackButtonRef = useRef<HTMLDivElement>(null);
  const [ldFlagsSet, setLdFlagsSet] = useState<boolean>(false);
  const ldClient = useLDClient();
  const ldFlags = useFlags();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const [toast, setToast] = useState<TpToastProps>();
  const setToastOpen = (open: boolean) => {
    if (toast) setToast({
      ...toast,
      open
    });
  };
  const safeShowIntercom = () => {
    try {
      showNewMessage('');
    } catch (error) {}
  };
  const safeIntercomDisplayName = (val: unknown): string | undefined => {
    // Cognito user ID is a 36-character UUID, just sets as undefined which Intercom handles gracefully
    // Prevents it from calling the user {guid} for his name.
    return typeof val === 'string' && /^[0-9a-f-]{36}$/i.test(val) ? undefined : val as string;
  };
  const safeHideIntercom = () => {
    try {
      hideIntercom();
    } catch (error) {}
  };
  const safeShutdownIntercom = () => {
    try {
      shutdownIntercom();
    } catch (error) {}
  };
  useEffect(() => {
    if (!authState?.isAuthenticated || !authState?.accessToken?.accessToken?.trim() || !authState?.idToken?.idToken?.trim()) {
      setIntercomJwt(null);
      return;
    }
    const ac = new AbortController();
    const getIntercomJwt = async () => {
      try {
        const response = await FetchWithVersionCheck('/api/protected/intercom', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authState?.accessToken?.accessToken}`,
            'x-tai-id-token': authState?.idToken?.idToken || ''
          },
          signal: ac.signal
        });
        if (ac.signal.aborted) return;
        if (!response.ok) {
          let detail = '';
          try {
            const errBody = (await response.json()) as {
              error?: unknown;
            };
            if (typeof errBody?.error === 'string' && errBody.error) {
              detail = errBody.error;
            }
          } catch {
            /* non-JSON or empty body */
          }
          const msg = `Intercom JWT request failed (${response.status})${detail ? `: ${detail}` : ''}`;
          if (response.status >= 500) {
            CaptureError(msg, undefined, !!featureFlags?.useIntercom);
          } else {
            CaptureWarning(msg, undefined, !!featureFlags?.useIntercom);
          }
          setIntercomJwt(null);
          return;
        }
        let data: {
          token?: unknown;
        } = {};
        try {
          data = (await response.json()) as {
            token?: unknown;
          };
        } catch {
          if (!ac.signal.aborted) {
            CaptureError('Intercom JWT response was not valid JSON', undefined, !!featureFlags?.useIntercom);
            setIntercomJwt(null);
          }
          return;
        }
        if (ac.signal.aborted) return;
        if (typeof data?.token === 'string' && data.token) {
          setCookie('intercomJwt', data.token);
          setIntercomJwt(data.token);
        } else {
          CaptureWarning('Intercom JWT success response missing token', undefined, !!featureFlags?.useIntercom);
          setIntercomJwt(null);
        }
      } catch (e) {
        if ((e as Error)?.name === 'AbortError') return;
        setIntercomJwt(null);
      }
    };
    void getIntercomJwt();
    return () => ac.abort();
  }, [authState?.isAuthenticated, authState?.idToken?.idToken, authState?.accessToken?.accessToken, featureFlags?.useIntercom]);

  /** Re-fetch before server JWT expiry (12h) so Intercom identity stays valid without an access-token rotation. */
  useEffect(() => {
    if (!intercomJwt?.trim()) return;
    const expMs = intercomJwtExpiryMs(intercomJwt);
    const delay = expMs != null ? Math.max(expMs - Date.now() - INTERCOM_JWT_REFRESH_BEFORE_EXP_MS, INTERCOM_JWT_REFRESH_MIN_DELAY_MS) : INTERCOM_JWT_REFRESH_FALLBACK_MS;
    const id = window.setTimeout(() => {
      const {
        access,
        idToken
      } = intercomAuthHeadersRef.current;
      if (!access?.trim() || !idToken?.trim()) return;
      void (async () => {
        try {
          const response = await FetchWithVersionCheck('/api/protected/intercom', {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${access}`,
              'x-tai-id-token': idToken
            }
          });
          if (!response.ok) {
            let detail = '';
            try {
              const errBody = (await response.json()) as {
                error?: unknown;
              };
              if (typeof errBody?.error === 'string' && errBody.error) {
                detail = errBody.error;
              }
            } catch {
              /* non-JSON or empty body */
            }
            const msg = `Intercom JWT refresh failed (${response.status})${detail ? `: ${detail}` : ''}`;
            if (response.status >= 500) {
              CaptureError(msg, undefined, !!featureFlags?.useIntercom);
            } else {
              CaptureWarning(msg, undefined, !!featureFlags?.useIntercom);
            }
            setIntercomJwt(null);
            return;
          }
          let data: {
            token?: unknown;
          } = {};
          try {
            data = (await response.json()) as {
              token?: unknown;
            };
          } catch {
            CaptureError('Intercom JWT refresh response was not valid JSON', undefined, !!featureFlags?.useIntercom);
            setIntercomJwt(null);
            return;
          }
          if (typeof data?.token === 'string' && data.token) {
            setCookie('intercomJwt', data.token);
            setIntercomJwt(data.token);
          } else {
            CaptureWarning('Intercom JWT refresh success response missing token', undefined, !!featureFlags?.useIntercom);
            setIntercomJwt(null);
          }
        } catch {
          setIntercomJwt(null);
        }
      })();
    }, delay);
    return () => clearTimeout(id);
  }, [intercomJwt, featureFlags?.useIntercom]);
  useEffect(() => {
    const ldIdentify = async () => {
      if (authState && authState.idToken?.claims.email && authState.idToken?.claims.email !== '' && authState.idToken?.claims.name && authState.idToken?.claims.name !== '') {
        const email = authState?.idToken?.claims.email;
        const name = authState?.idToken?.claims.name;
        const tenantId = authState?.accessToken?.claims?.['custom:tenant'];
        const tenantName = authState?.accessToken?.claims?.['custom:tenant_name'];
        const baseEmail = email?.includes('+') && email?.includes('@') ? `${email?.split('+')[0]}@${email?.split('@')[1]}` : email;
        const ldContext: LDMultiKindContext = {
          kind: 'multi',
          user: {
            key: email ?? '',
            name: name ?? '',
            email: email ?? '',
            baseEmail: baseEmail ?? ''
          },
          organization: {
            key: features?.orgId ? `${features?.orgId}` : undefined,
            systemStage: features?.systemStage ?? ''
          }
        };
        // add tenant id to organization context if it exists
        if (tenantId) {
          (ldContext.organization as any).tenant = {
            key: tenantId ?? ''
          };
        }
        if (tenantName) {
          setTenantName(tenantName as string);
          (ldContext.organization as any).tenantName = {
            key: tenantName ?? ''
          };
        } else setTenantName(undefined);
        const ldFlagsSet = await ldClient?.identify(ldContext);
        if (ldFlagsSet) {
          setLdFlagsSet(true);
        } else {
          setLdFlagsSet(false);
        }
      }
    };
    ldIdentify();
  }, [ldClient, features?.orgId, authState?.idToken?.claims.email, authState?.idToken?.claims.name, authState?.accessToken?.claims?.['custom:tenant'], authState?.accessToken?.claims?.['custom:tenant_name']]);
  useEffect(() => {
    if (banners && banners.length > 0) {
      if (banners.includes('cjis')) {
        setIsBannerModalOpen(true);
      }
    }
  }, [banners]);
  useEffect(() => {
    // This will run after every render
    if (featureFlags?.logoutOnTenantMismatch && isAuthInitialized && pathname.match(/\/tenant\/[^/]+/)) {
      const tenantFromUrl = pathname.split('?')[0].split('/tenant/')[1].split('/')[0].toLowerCase().trim() || '';
      const tenantFromIdToken = ((authState?.accessToken?.claims?.['custom:tenant_name'] || '') as string)?.toLowerCase().trim();
      if (tenantFromUrl && tenantFromUrl !== '' && tenantFromIdToken && tenantFromIdToken !== '' && tenantFromUrl !== tenantFromIdToken) {
        console.log(`Tenant mismatch detected: ${tenantFromUrl} (URL) !== ${tenantFromIdToken} (Token) - logging out`);
        if (authState?.idToken?.claims?.email?.toLowerCase().startsWith('robertk') || authState?.idToken?.claims?.email?.toLowerCase().endsWith('@tranquility-ai.com')) {
          console.log('Would have logged any other user out...');
        } else {
          handleLogout();
        }
      }
    }
  }, [featureFlags?.logoutOnTenantMismatch, isAuthInitialized, pathname, authState?.accessToken?.claims?.['custom:tenant_name']]);

  // Keep authState in sync with tokens from alb-info (initial load and periodic refresh for tenant + CSM).
  // When the 50-min refresh returns new tokens, features.tokens updates; we must push them into authState
  // so API calls and AuthContext use the refreshed token.
  useEffect(() => {
    // console.log('🔍 [DEBUG] Cognito auth setup useEffect triggered:', {
    //   hasFeatures: !!features,
    //   isCognitoClient: features?.isCognitoClient,
    //   hasTokens: !!features?.tokens,
    //   tokensKeys: features?.tokens ? Object.keys(features.tokens) : []
    // });

    if (!features || !features?.isCognitoClient) {
      return;
    }
    const cognitoId = (features.tokens as any)?.decodedIdToken;
    const cognitoAccess = (features.tokens as any)?.decodedAccessToken;
    const realAccessToken = (features.tokens as any)?.rawAccessToken;
    const idToken = (features.tokens as any)?.rawIdToken || (features.tokens as any)?.rawIdTokenData;
    setCognitoClientId(cognitoAccess?.client_id);
    const username = cognitoId?.['cognito:username'] || cognitoId?.username;
    const name = cognitoId?.name || cognitoId?.email?.split('@')[0] || username;
    if (!(cognitoId?.email && username && idToken && idToken.trim() !== '')) {
      return;
    }
    const authStateToSet = {
      idToken: {
        issuer: cognitoId.iss,
        clientId: cognitoId.aud,
        expiresAt: cognitoId.exp,
        idToken: idToken as any,
        authorizeUrl: cognitoId.authorizeUrl,
        scopes: cognitoId.scopes,
        claims: {
          email: cognitoId.email,
          name: name,
          aud: 'timepilot-cognito',
          auth_time: cognitoId.auth_time,
          exp: cognitoId.exp,
          iss: cognitoId.iss,
          jti: cognitoId.jti,
          iat: cognitoId.iat,
          email_verified: true,
          family_name: cognitoId.family_name ?? '',
          given_name: cognitoId.given_name ?? '',
          sub: username
        }
      },
      accessToken: {
        // TODO: use (realAccessToken || idToken) once all consumers (e.g. cases route, getTokenInfoFromHeadersOrCookies) use id token for tenant
        accessToken: idToken as any,
        expiresAt: cognitoAccess?.exp ?? cognitoId.exp,
        claims: cognitoId,
        tokenType: 'Bearer',
        userinfoUrl: cognitoId.userinfoUrl,
        authorizeUrl: cognitoId.authorizeUrl,
        scopes: cognitoId.scopes
      },
      isAuthenticated: true
    };
    const setAuthCookiesCallback = async () => {
      await setAuthCookies(authStateToSet as AuthState);
    };
    setAuthCookiesCallback();
    setTenantId(cognitoId['custom:tenant']);
    // TODO: use (realAccessToken || idToken) once consumers use id token for tenant; for now idToken so cookie has tenant claims
    setAccessToken(idToken as string);
    setRealAccessToken(realAccessToken as string);
    setAuthState(authStateToSet);
    setIsCognitoUser(true);
    setIsAuthInitialized(true);
    // CSM/super admins: only store token for Heimdall when it is from the employee pool.
    // In tenant context (e.g. tenant1.jen...), features.tokens is the tenant pool token; storing
    // it would make us send the wrong token to Heimdall. So we only store when the raw token
    // actually decodes to employee pool iss (double-check to avoid ever storing a tenant token).
    const role = cognitoId['custom:role'];
    const isSuperAdminFlag = cognitoId['custom:is_super_admin'] === 'true';
    const isCsmOrSuperAdmin = role === 'csm' || role === 'super_admin' || isSuperAdminFlag;
    const employeePoolIssuer = ((features as any)?.employeePoolIssuer ?? (typeof process !== 'undefined' ? process.env.NEXT_PUBLIC_COGNITO_EMPLOYEE_POOL_ISSUER ?? '' : '') ?? '').trim();
    const isEmployeePoolToken = !!employeePoolIssuer && (cognitoId.iss ?? '').trim() === employeePoolIssuer;
    let rawTokenIsEmployeePool = false;
    if (idToken && typeof idToken === 'string' && employeePoolIssuer) {
      const decoded = decodeJwtPayload(idToken as string);
      if (decoded) {
        rawTokenIsEmployeePool = (decoded.iss as string ?? '').trim() === employeePoolIssuer;
      }
    }
    if (isCsmOrSuperAdmin) {
      // Store access token for Heimdall admin (orgs/tenants); ID token can cause 403 / "User not found or inactive".
      if (isEmployeePoolToken && rawTokenIsEmployeePool) {
        setEmployeeTokenForHeimdall((realAccessToken || idToken) as string);
      }
      setCookie('tai_csm_user', '1', {
        maxAge: 60 * 60 * 24
      }); // 24h — token-expired 401 can redirect to /csm
    } else {
      if (isEmployeePoolToken) {
        clearEmployeeTokenForHeimdall();
      }
      deleteCookie('tai_csm_user');
    }
    // Depend on token content so authState updates when periodic alb-info refresh returns new tokens
  }, [features?.isCognitoClient, features?.tokens, (features?.tokens as any)?.rawIdToken, (features?.tokens as any)?.rawAccessToken, (features?.tokens as any)?.decodedIdToken?.exp, (features?.tokens as any)?.decodedAccessToken?.exp]);

  // Proactively fetch employee pool token when we have CSM/super_admin but only tenant token.
  // features.tokens always comes from /api/auth/alb-info, which is NOT under /csm/*, so the ALB
  // uses the tenant pool and we never get an employee token there. Fetching /csm/auth-info hits
  // the /csm/* rule so the server receives employee pool auth; if we get 200 we store the token.
  const employeeTokenFetchAttemptedRef = useRef(false);
  const employeeTokenFetchCancelledRef = useRef(false);
  useEffect(() => {
    if (!features?.isCognitoClient || !features?.tokens) return;
    const cognitoId = (features.tokens as any)?.decodedIdToken;
    const idToken = (features.tokens as any)?.rawIdToken || (features.tokens as any)?.rawIdTokenData;
    const employeePoolIssuer = ((features as any)?.employeePoolIssuer ?? '').trim();
    if (!employeePoolIssuer || !cognitoId?.iss || !idToken) return;
    const role = cognitoId['custom:role'];
    const isSuperAdmin = cognitoId['custom:is_super_admin'] === 'true';
    const isCSM = role === 'csm' || role === 'super_admin' || isSuperAdmin;
    if (!isCSM) return;
    const isTenantToken = cognitoId.iss !== employeePoolIssuer;
    if (!isTenantToken) return; // already have employee token in context
    if (getEmployeeTokenForHeimdall()) return; // already stored
    if (employeeTokenFetchAttemptedRef.current) return;
    employeeTokenFetchAttemptedRef.current = true;
    employeeTokenFetchCancelledRef.current = false;
    (async () => {
      try {
        const res = await fetch('/csm/auth-info', {
          cache: 'no-store'
        });
        if (!res.ok || employeeTokenFetchCancelledRef.current) {
          employeeTokenFetchAttemptedRef.current = false;
          return;
        }
        const data = await res.json();
        if (employeeTokenFetchCancelledRef.current) {
          employeeTokenFetchAttemptedRef.current = false;
          return;
        }
        const rawAccessToken = (data.tokens?.rawAccessToken ?? '').trim();
        const rawIdToken = (data.tokens?.rawIdToken ?? '').trim();
        const tokenForIssuerCheck = rawAccessToken || rawIdToken;
        if (!tokenForIssuerCheck) {
          employeeTokenFetchAttemptedRef.current = false;
          return;
        }
        const decoded = decodeJwtPayload(tokenForIssuerCheck);
        if (!decoded) {
          employeeTokenFetchAttemptedRef.current = false;
          return;
        }
        const tokenIss = (decoded.iss as string ?? '').trim();
        if (tokenIss === employeePoolIssuer) {
          setEmployeeTokenForHeimdall(rawAccessToken || rawIdToken);
        } else {
          employeeTokenFetchAttemptedRef.current = false;
        }
      } catch {
        employeeTokenFetchAttemptedRef.current = false;
      }
    })();
    return () => {
      employeeTokenFetchCancelledRef.current = true;
    };
  }, [features?.isCognitoClient, features?.tokens, (features as any)?.employeePoolIssuer]);
  useEffect(() => {
    if (!!tenantName && tenantName.trim() !== '') {
      if (!!remoteStorageLink?.url && remoteStorageLink?.url.trim() !== '') {
        setRemoteStorageLinkLocal({
          type: remoteStorageLink?.type ?? 'SharePoint',
          url: remoteStorageLink?.url
        });
      } else {
        setRemoteStorageLinkLocal(null);
      }
    } else if (!!featureFlags?.sharepointLink && (featureFlags?.sharepointLink as string).trim() !== '') {
      // not a real flag, comes from ENV variables for the organization
      setRemoteStorageLinkLocal({
        type: (featureFlags?.sharepointLink as string).toLowerCase().includes('google') ? 'Google Drive' : 'SharePoint',
        url: featureFlags?.sharepointLink as string
      });
    } else {
      setRemoteStorageLinkLocal(null);
    }
  }, [tenantName, featureFlags?.sharepointLink, remoteStorageLink]);

  // merge feature flags and ld flags into featureFlags
  useEffect(() => {
    const initFeatureFlags = () => {
      if (!!ldClient && features?.ldClientSideId && features?.orgId && !!ldFlags && !!ldFlagsSet) {
        const newFeatureFlags = {
          ...featureFlags,
          ...features,
          ...ldFlags,
          ...flagOverrides
        };
        setFeatureFlags(newFeatureFlags);
      }
    };
    if (!!ldClient && authState && authState.idToken?.claims.email && authState.idToken?.claims.email !== '' && authState.idToken?.claims.name && authState.idToken?.claims.name !== '' && features?.ldClientSideId && features?.orgId && !!ldFlags) {
      initFeatureFlags();
    } else {
      setFeatureFlags({
        ...featureFlags,
        ...features,
        ...ldFlags,
        ...flagOverrides
      });
    }
    if (ldFlags['logTenantId']) {
      console.log('🔍 [DEBUG] Tenant ID:', tenantId);
    }
    if (caseId) {
      (async () => {
        if (currentTimelineId && currentTimelineId !== '') {
          await fetchNewTimeline(currentTimelineId, caseId, false);
          setSelectedSecondOption(currentTimelineId);
          setCurrentTimelineId(currentTimelineId);
        }
      })();
    }
  }, [authState, features, ldFlags, ldClient, ldFlagsSet, flagOverrides]);
  useEffect(() => {
    const raw = featureFlags?.useIntercom;
    if (raw === undefined || raw === null) return;
    void setCookie('useIntercom', String(!!raw), {
      maxAge: 60 * 60 * 24 * 365,
      path: '/'
    });
  }, [featureFlags?.useIntercom]);
  const showLoginBranch = !(isAuthInitialized && authState?.isAuthenticated && authState?.accessToken?.accessToken && authState?.accessToken?.accessToken.trim() !== '' && authState?.idToken?.idToken && authState?.idToken?.idToken.trim() !== '');

  // Fetch user admin status
  useEffect(() => {
    const fetchUserAdminStatus = async () => {
      if (!authState?.isAuthenticated) {
        setIsUserAdmin(false);
        setIsUserServiceAvailable(false);
        return;
      }
      try {
        const response = await fetch('/api/protected/user', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        if (response.ok) {
          const data = await response.json();
          const user = data.response;
          setIsUserAdmin(user?.is_admin || false);
          setIsUserServiceAvailable(true);
        } else {
          const errorText = await response.text();
          console.warn('⚠️ [DEBUG] Failed to fetch user admin status:', {
            status: response.status,
            statusText: response.statusText,
            error: errorText
          });
          setIsUserAdmin(false);
          setIsUserServiceAvailable(false);
        }
      } catch (error) {
        console.error('Failed to fetch user admin status:', error);
        setIsUserAdmin(false);
        setIsUserServiceAvailable(false);
      }
    };
    const userServiceEnabled = featureFlags?.useUserService;
    if (userServiceEnabled) {
      fetchUserAdminStatus();
    } else {
      // Reset state when feature flag is disabled
      setIsUserServiceAvailable(false);
    }
  }, [authState?.isAuthenticated, featureFlags]);
  useEffect(() => {
    const intercomOn = !!featureFlags?.useIntercom && authState?.isAuthenticated && !!authState?.idToken;
    const intercomUserReady = !!authState?.idToken?.claims.email && authState?.idToken?.claims.email !== '' && !!authState?.idToken?.claims.name && authState?.idToken?.claims.name !== '';
    if (!intercomOn || !intercomUserReady || selectedView === 'presentation') {
      safeHideIntercom();
      return;
    }
    if (intercomJwt) {
      const intercomInit = {
        intercom_user_jwt: intercomJwt,
        app_id: 'bab3jdz3',
        user_id: authState?.idToken?.claims.email,
        name: safeIntercomDisplayName(authState?.idToken?.claims.name),
        email: authState?.idToken?.claims.email,
        chat_session_id: chatSessionId ?? '',
        case_id: caseId ?? '',
        selected_view: selectedView ?? ''
      };
      Intercom(intercomInit);
    } else {
      // JWT still loading or refresh in flight — do not shutdown (avoids widget flicker).
      safeHideIntercom();
    }
  }, [intercomJwt, authState?.isAuthenticated, featureFlags?.useIntercom, authState?.idToken, authState?.idToken?.claims.email, authState?.idToken?.claims.name, selectedView, chatSessionId, caseId]);
  const userIdentity = {
    userId: authState?.idToken?.claims?.sub as string | undefined ?? '',
    email: authState?.idToken?.claims?.email as string | undefined ?? '',
    userName: authState?.idToken?.claims?.name as string | undefined ?? '',
    tenantId: authState?.accessToken?.claims?.['custom:tenant'] as string | undefined ?? tenantId ?? '',
    tenantName: authState?.accessToken?.claims?.['custom:tenant_name'] as string | undefined ?? tenantName ?? '',
    orgId: features?.orgId as string | undefined ?? '',
    systemStage: features?.systemStage as string | undefined ?? ''
  };
  useIntercomTicketForm({
    enabled: !!featureFlags?.useIntercom && !!authState?.isAuthenticated,
    accessToken: authState?.accessToken?.accessToken,
    idToken: authState?.idToken?.idToken,
    caseId: caseId ?? '',
    selectedView: selectedView ?? '',
    chatSessionId: chatSessionId ?? '',
    identity: userIdentity
  });

  // 🔍 TEMPORARY DEBUG: Force Cognito mode detection based on ALB headers
  useEffect(() => {
    const detectCognitoUser = () => {
      try {
        // Only set as Cognito user if we can verify from the actual token
        // Check if the token issuer contains 'cognito' (for Cognito users)
        const tokenIssuer = authState?.idToken?.issuer || authState?.idToken?.claims?.iss;
        if (tokenIssuer && typeof tokenIssuer === 'string') {
          const isCognito = tokenIssuer.includes('cognito');
          console.log('🔍 [DEBUG] Token issuer:', tokenIssuer);
          console.log('🔍 [DEBUG] Is Cognito user:', isCognito);
          // Token issuer used only for Cognito vs Okta detection

          if (isCognito && !isCognitoUser) {
            console.log('🔍 [DEBUG] Setting Cognito mode based on token issuer');
            setIsCognitoUser(true);
          } else if (!isCognito && isCognitoUser) {
            console.log('🔍 [DEBUG] Setting Okta mode based on token issuer');
            setIsCognitoUser(false);
          }
        }
      } catch (error) {
        console.warn('🔍 [DEBUG] Failed to detect Cognito mode:', error);
      }
    };

    // Only run detection if we have an authenticated user
    if (authState?.isAuthenticated) {
      detectCognitoUser();
    }
  }, [authState?.isAuthenticated, authState?.idToken, isCognitoUser]);
  useEffect(() => {
    if (featureFlags?.workProductNav && selectedView === 'work-product-index' && caseId && caseId.trim() !== '') {
      try {
        FetchWithVersionCheck(`/api/protected/threatReport/${encodeURIComponent(caseId || '')}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
            'x-tai-id-token': authState?.idToken?.idToken || ''
          }
        }).then(async response => {
          if (response.ok) {
            const data = await response.json();
            if (data && data.epoch_time_secs) {
              setThreatReportJson(data);
            } else {
              setThreatReportJson(null);
            }
          } else {
            setThreatReportJson(null);
          }
        });
      } catch (error) {
        setThreatReportJson(null);
      }
    } else {
      setThreatReportJson(null);
    }
  }, [caseId, featureFlags?.workProductNav, selectedView]);
  useEffect(() => {
    let entityExtractionJobsWithQuestions: any[] = [];
    if (chatHistorySessions && chatHistorySessions.length > 0 && entityExtractionJobs.length > 0) {
      entityExtractionJobs.forEach((job: any) => {
        const epochTimeSecs = Number(job.epochTimeSecs);
        const newAnswerEpochTimeSecs = Number(newAnswer?.epochTimeSecs);
        if (newAnswerEpochTimeSecs === epochTimeSecs) {
          entityExtractionJobsWithQuestions.push({
            ...job,
            question: newAnswer?.question ?? ''
          });
        } else {
          chatHistorySessions.forEach((session: any) => {
            session.questions.forEach((question: any) => {
              if (Number(question.epochTime) === epochTimeSecs) {
                entityExtractionJobsWithQuestions.push({
                  ...job,
                  question: question.question
                });
              }
            });
          });
        }
      });
    }
    if (entityServicePollAllJobsCount > 0 && entityExtractionJobsWithQuestions.length > 0) {
      if (!entityExtractionJobsWithQuestions.some((job: any) => job.status === 'pending')) {
        setEntityServicePollAllJobsCount(0);
      }
    }
    setEntityExtractionJobsWithQuestions(entityExtractionJobsWithQuestions);
  }, [entityExtractionJobs, chatHistorySessions, newAnswer]);
  const setMainChatSessionId = (sessionId: string | null) => {
    setChatSessionId(sessionId);
  };
  useEffect(() => {
    const fetchChatHistory = async () => {
      setChatHistorySessions(null);
      const response = await FetchWithVersionCheck(`/api/protected/sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'x-tai-id-token': authState?.idToken?.idToken || ''
        },
        body: JSON.stringify({
          caseId,
          userId: authState?.idToken?.claims.email ?? ''
        })
      });
      const data = await response.json();
      const sessions = data.sessions ? Object.values(data.sessions) : [];
      if (sessions) {
        const sessionData = sessions ? Object.values(sessions) : [];
        const sortedSessionData = sessionData.filter((session: any) => session.questions.length > 0).sort((a: any, b: any) => {
          if (a.sessionId === chatSessionId) {
            return -1;
          }
          if (b.sessionId === chatSessionId) {
            return 1;
          }
          const aLastQuestion = a.questions[a.questions.length - 1];
          const bLastQuestion = b.questions[b.questions.length - 1];
          return bLastQuestion.epochTime - aLastQuestion.epochTime;
        });
        setChatHistorySessions(sortedSessionData);
      }
    };
    if (caseId && caseId !== '' && authState?.idToken?.claims.email && authState?.idToken?.claims.email !== '') {
      fetchChatHistory();
    }
  }, [caseId, authState?.idToken?.claims.email, chatSessionId]);
  const swapSessionId = async (sessionId: string | null, newSessionId: string): Promise<string | null> => {
    try {
      const historyResponse = await FetchWithVersionCheck('/api/protected/chat/history', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'x-tai-id-token': authState?.idToken?.idToken || ''
        },
        body: JSON.stringify({
          userId: authState?.idToken?.claims.email ?? '',
          caseId: caseId ?? '',
          sessionId: newSessionId,
          deleteSessionId: sessionId ?? null
        })
      });
      const historyResponseJson = await historyResponse.json();
      return historyResponseJson.sessionId ?? null;
    } catch (error) {
      return null;
    }
  };
  useEffect(() => {
    const allSources = sourcesFromChat;
    const mediaSources = sourcesFromChat.map((source: SourceObject): MediaObject => {
      const mediaType: string = getMediaType(source, source.sourceInfo);
      const mediaObjectFromSource: MediaObject = {
        ...source,
        mediaType: mediaType as MediaType,
        mediaSrc: source.sourceUrl,
        mediaUrl: source.sourceUrl,
        mediaContext: source.sourceContext ? {
          ...source.sourceContext
        } : {}
      };
      if (source.pageNumber) {
        mediaObjectFromSource.mediaContext!.pageNumber = source.pageNumber;
      }
      if (source.previewFilePath && source.previewFilePath !== '') {
        mediaObjectFromSource.mediaContext!.previewUrl = source.previewFilePath;
      }
      return mediaObjectFromSource;
    });
    const dedupedSources = dedupeSources(allSources);
    const dedupedMediaSources = dedupeMediaSources(mediaSources);
    setAllSourcesFromChat({
      allSources: dedupedSources,
      mediaSources: dedupedMediaSources
    });
  }, [sourcesFromChat]);
  useEffect(() => {
    if (addEvent?.id && addEvent?.id.split('_')[0] === 'NEW') {
      setEventIndex(-1);
      handleOpenEventModal();
    }
  }, [addEvent]);
  const openTitleSlideModal = () => {
    setIsTitleSlideModalOpen(true);
  };
  const closeTitleSlideModal = () => {
    setIsTitleSlideModalOpen(false);
  };
  const openModalFromTable = (event: Slide, column?: string | null) => {
    setCurrentColumn(column || null);
    const isNewEventFromChat = event && event.id && event.id.includes('NEW_EVENT_FROM_CHAT');
    const isNewEventFromTable = column === 'create' || event && event.id && event.id.includes('NEW_EVENT_FROM_TABLE');
    const isDuplicateEventFromTable = column === 'duplicate' || event && event.id && event.id.includes('NEW_DUPLICATE_FROM_TABLE');
    const isNewEvent = isNewEventFromChat || isNewEventFromTable || isDuplicateEventFromTable;
    if (column !== 'timelineTitle' && !isNewEvent) {
      setEventIndex(event.displayOrder ? event.displayOrder - 1 : -1);
    }
    if (isDuplicateEventFromTable) {
      setCurrentColumn('duplicate');
    } else if (isNewEventFromChat) {
      setCurrentColumn('newEventFromChat');
    } else if (isNewEventFromTable) {
      setCurrentColumn('create');
    }
    setIsEventModalOpen(true);
  };
  const openModalFromPresentation = (slide: Slide, column: string | null) => {
    setCurrentColumn(column);
    setEventIndex(slide.displayOrder ? slide.displayOrder - 1 : -1);
    setIsEventModalOpen(true);
  };
  const handleEditTimelineTitle = () => {
    const titleSlide: Slide = {
      id: 'TITLE_SLIDE',
      displayOrder: 1,
      slideTitle: title,
      slideDescription: description,
      media: [{
        mediaSrc: media,
        mediaType: 'image' as MediaType
      }],
      show: true
    };
    if (selectedView === 'table') {
      openModalFromTable(titleSlide, 'timelineTitle');
    } else if (selectedView === 'presentation' || selectedView === 'courtroom') {
      openModalFromPresentation(titleSlide, 'timelineTitle');
    }
  };
  const handleOpenEventModal = () => {
    setIsEventModalOpen(true);
  };
  const handleCloseEventModal = () => {
    setIsEventModalOpen(false);
    // If user came from timeline selection flow, dismiss that modal too
    if (pleaseSelectTimeline || chatEventToAdd) {
      setPleaseSelectTimeline(false);
      setChatEventToAdd(undefined);
      setModalSelectedTimeline(undefined);
    }
  };
  const fetchNewTimeline = async (documentIdentifier: string | undefined, caseId: string | undefined, isUpdating: boolean, creatingEventFromChat?: boolean) => {
    setTimelineTags([]);
    setIsLegacy(false);
    setLoadingTimeline(true);
    setPleaseSelectTimeline(false);
    if (!isUpdating) {
      setNewTimelineIsAvailable(false);
      setAllSlides(undefined);
      setLegacyTimelineIsAvailable(false);
      setCurrentTimelineId(undefined);
    }
    if ((!documentIdentifier || documentIdentifier && documentIdentifier.trim() === '') && (!caseId || caseId && caseId.trim() === '')) return;
    try {
      const request: any = await FetchWithVersionCheck("/api/protected/timelines", {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'x-tai-id-token': authState?.idToken?.idToken || ''
        },
        cache: 'no-store',
        body: JSON.stringify({
          docId: documentIdentifier,
          tags: `CASE:${caseId}`,
          tenantId: tenantId
        })
      });
      const response = await request.json();
      const noTimelinesFound = response && response.error && response.error.includes('No timelines found');
      if (response && (noTimelinesFound || response.timeline || response.timelineList)) {
        setTimelineServiceAppearsOperational(true);
      } else {
        setTimelineServiceAppearsOperational(false);
      }
      if (response && (response.timelineList && response.timelineList.length > 0 || response.timeline)) {
        const timelineList = response.timelineList || [response.timeline];
        setMultipleTimelinesAvailable(timelineList);
        if (response?.timeline?.id) {
          setSelectedSecondOption(response?.timeline?.id);
          setLoadingTimeline(false);
        }
      } else {
        setNewTimelineIsAvailable(false);
        setLoadingTimeline(false);
        return;
      }
      const timeline = response?.timeline;
      if (!timeline) {
        setLoadingTimeline(false);
        return;
      }
      const tags = timeline?.tags;
      setTimelineTags(tags);
      const events = response?.timeline?.events;
      setTimeline(timeline);
      let newEvents = 0;
      const unsortedSlides = [];
      let useLocalDisplayOrder = true;
      if (events[0] && events[0].displayOrder !== undefined) {
        useLocalDisplayOrder = false;
      }
      setSortByDateTime(useLocalDisplayOrder);
      let localDisplayOrder = 0;
      for (const event of events) {
        if (event.deleted) continue;
        localDisplayOrder += 1;
        newEvents += 1;
        // if timezone display/editing is supported, we don't display the time offset by the eventLocalTimezone
        const {
          displayDate,
          time,
          localizedDateTime
        } = getDisplayDateAndTime(event.eventDateTime, null);
        unsortedSlides.push({
          show: event.hidden ? false : true,
          order: useLocalDisplayOrder ? localDisplayOrder : event.displayOrder,
          displayOrder: useLocalDisplayOrder ? localDisplayOrder : event.displayOrder,
          approval: event.approved,
          id: event.id ? event.id : `NEW_${newEvents}`,
          date: event.eventDateTime,
          displayDate: displayDate,
          time: time,
          isTimeValid: event.isTimeValid !== true ? false : true,
          localizedDateTime: localizedDateTime,
          eventLocalTimezone: event.eventLocalTimezone,
          eventType: event.eventType,
          slideTitle: event.slideTitle,
          slideSubTitle: event.slideSubTitle,
          slideDescription: event.slideDescription,
          media: event.media ? filterMedia(event.media) : event.mediaType && event.mediaUrl ? [{
            mediaType: event.mediaType as MediaType,
            mediaSrc: event.mediaSrc || event.mediaUrl,
            mediaUrl: event.mediaSrc || event.mediaUrl
          }] : [],
          mediaSrc: event.media && filterMedia(event.media)[0] ? filterMedia(event.media)[0].mediaSrc || filterMedia(event.media)[0].mediaUrl : undefined,
          mediaType: event.media && filterMedia(event.media)[0] ? filterMedia(event.media)[0].mediaType : undefined,
          location: event.location,
          sources: event.sources ? filterSources(event.sources) : [],
          createdAt: event.createdAt,
          updatedAt: event.updatedAt
        });
      }
      if (response.timeline.title) {
        setTitle(response.timeline.title);
      } else {
        setTitle(undefined);
      }
      if (response.timeline.description) {
        setDescription(response.timeline.description);
      } else {
        setDescription(undefined);
      }
      if (response.timeline.media) {
        setMedia(response.timeline.media);
      } else {
        setMedia(undefined);
      }
      setIsEditableTimeline(true);
      setCurrentTimelineId(response.timeline.id);
      const sortedSlides = getSortedSlides(unsortedSlides, useLocalDisplayOrder ? 'datetime' : 'displayOrder');
      setAllSlides(sortedSlides);
      setTimelinePatch({
        tags: timelineTags,
        title: response.timeline.title,
        description: response.timeline.description,
        media: response.timeline.media
      });
      setNewTimelineIsAvailable(true);
      setDocIdMode(false);
      if (!creatingEventFromChat) {
        setLoadingTimeline(false);
      } else if (creatingEventFromChat && chatEventToAdd) {
        proceedWithChatEvent(response.timeline.id);
        if (!chatEventToAdd.autogen || !creatingEventFromChat) {
          setLoadingTimeline(false);
        }
      } else if (creatingEventFromChat && parsedEventsFromChat) {
        createEventsFromParsedEvents(parsedEventsFromChat, useEnrichment, false, response.timeline.id);
      } else {
        setLoadingTimeline(false);
      }
    } catch (error) {
      setNewTimelineIsAvailable(false);
      setLoadingTimeline(false);
      const errorMessage = 'Error fetching new timeline.';
      CaptureError(errorMessage, error as Error, !!featureFlags?.useIntercom);
    }
  };

  // Fetch work products (timelines list) for a specific case - lightweight version
  const fetchWorkProductsForCase = async (caseId: string): Promise<TimelineDropdownItem[]> => {
    if (!caseId || caseId.trim() === '' || !accessToken || !authState?.idToken?.idToken) {
      return [];
    }
    try {
      const request: any = await FetchWithVersionCheck("/api/protected/timelines", {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'x-tai-id-token': authState.idToken.idToken
        },
        cache: 'no-store',
        body: JSON.stringify({
          docId: undefined,
          tags: `CASE:${caseId}`,
          tenantId: tenantId
        })
      });
      const response = await request.json();
      if (response && response.timelineList && response.timelineList.length > 0) {
        return response.timelineList;
      } else if (response && response.timeline) {
        return [response.timeline];
      }
      return [];
    } catch (error) {
      // Silently fail for pre-fetching - don't spam errors
      console.warn(`Error fetching work products for case ${caseId}:`, error);
      return [];
    }
  };
  const handleEditEvent = async (editedEvent: Slide) => {
    // check for time/date change, if so, refetch timeline
    let shouldRefetchTimeline = true;
    const currentEvent = allSlides?.find(event => event.id === editedEvent.id);
    const currentEventDate = currentEvent?.date ? getDateFromDateStr(currentEvent.date)?.toString() : null;
    const editedEventDate = editedEvent.date ? getDateFromDateStr(editedEvent.date)?.toString() : null;
    const currentEventTime = currentEvent?.time ? getClientTimeFromStr(currentEvent.time)?.toString() : null;
    const editedEventTime = editedEvent.time ? getClientTimeFromStr(editedEvent.time)?.toString() : null;
    if (currentEventTime !== editedEventTime) {
      shouldRefetchTimeline = true;
    } else if (currentEventDate !== editedEventDate) {
      shouldRefetchTimeline = true;
    } else {
      // shouldRefetchTimeline = false; should we ever not refectch
    }
    editedEvent.sources = editedEvent.sources ? editedEvent.sources.map((source: SourceObject) => {
      return {
        ...source,
        sourceType: Array.isArray(source.sourceType) ? source.sourceType[0] : source.sourceType
      };
    }) : undefined;
    editedEvent.media = editedEvent.media ? editedEvent.media.map((media: MediaObject) => {
      return {
        ...media,
        mediaType: Array.isArray(media.mediaType) ? media.mediaType[0] : media.mediaType
      };
    }) : undefined;
    if (!isLegacy) {
      if (!currentTimelineId) return;
      setLoadingTimeline(shouldRefetchTimeline);
      updateEvent(editedEvent, shouldRefetchTimeline);
    }
    if (allSlides) {
      if (!shouldRefetchTimeline) {
        setAllSlides(allSlides.map(event => event.id === editedEvent.id ? editedEvent : event));
      }
    } else {
      if (!shouldRefetchTimeline) {
        setAllSlides(undefined);
      }
    }
  };
  const patchTimeline = async (currentTimelineId: string, timelinePatchIn: TimelinePatch) => {
    try {
      const timelineUrl = `/api/protected/timelines`;
      const timelinePatchToSend = {
        ...timelinePatchIn,
        tags: timelineTags
      };
      if (timelinePatchToSend.media && typeof timelinePatchToSend.media !== 'string') {
        timelinePatchToSend.media = timelinePatchToSend.media?.[0]?.mediaSrc || undefined;
      } else if (timelinePatchToSend.media && typeof timelinePatchToSend.media === 'string') {
        timelinePatchToSend.media = timelinePatchToSend.media;
      }
      if (!timelinePatchToSend.tags || timelinePatchToSend.tags.length < 1) {
        return;
      }
      const response = await FetchWithVersionCheck(timelineUrl, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'x-tai-id-token': authState?.idToken?.idToken || ''
        },
        body: JSON.stringify({
          docId: currentTimelineId,
          patch: {
            tags: timelinePatchToSend.tags,
            title: timelinePatchToSend.title || undefined,
            description: timelinePatchToSend.description || undefined,
            media: timelinePatchToSend.media || undefined
          },
          tenantId: tenantId
        })
      });
      const patchResponse = await response.json();
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      await fetchNewTimeline(currentTimelineId, caseId, true);
    } catch (error) {
      setToast({
        setOpen: setToastOpen,
        open: true,
        location: 'bottom-right',
        time: Date.now(),
        duration: 3000,
        title: 'Title Slide Update Failed',
        description: 'Title slide update failed. Please refresh the page and try again.',
        level: ToastLevel.ERROR
      });
    }
  };
  const onUpdateTimeline = async (action: 'delete' | 'update' | 'order', rows: Slide[]) => {
    if (rows && rows.length > 0 && timeline && action === 'delete') {
      let deletedEvents = 0;
      try {
        for (const row of rows) {
          await deleteEvent(row, false);
          deletedEvents += 1;
        }
      } catch (error) {
        console.error('Error deleting events:', error);
      }
      if (deletedEvents !== rows.length) {
        setToast({
          setOpen: setToastOpen,
          open: true,
          location: 'bottom-right',
          time: Date.now(),
          duration: 3000,
          title: 'Some Events Were Not Deleted',
          description: 'Delete failed for some events. Please refresh the page and try again.',
          level: ToastLevel.ERROR
        });
      }
      await fetchNewTimeline(currentTimelineId, caseId, true);
    } else if (!isLegacy && !sortByDateTime && rows && rows.length > 0 && timeline && action === 'order') {
      const timelineUrl = `/api/protected/timelines`;
      const order = rows.map(row => {
        return row.id;
      });
      await FetchWithVersionCheck(timelineUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'x-tai-id-token': authState?.idToken?.idToken || ''
        },
        body: JSON.stringify({
          docId: currentTimelineId,
          action: 'order',
          order,
          tenantId: tenantId
        })
      });
      await fetchNewTimeline(currentTimelineId, caseId, true);
    }
  };
  const deleteTimeline = async (currentTimelineId: string) => {
    setLoadingTimeline(true);
    try {
      const timelineUrl = `/api/protected/timelines`;
      const body = {
        docId: currentTimelineId,
        action: 'delete',
        tenantId: tenantId
      };
      const response = await FetchWithVersionCheck(timelineUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'x-tai-id-token': authState?.idToken?.idToken || ''
        },
        body: JSON.stringify(body)
      });
      const deleteResponse = await response.json();
      setLoadingTimeline(false);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      setAllSlides(undefined);
      setCurrentTimelineId(undefined);
      setNewTimelineIsAvailable(false);
      setLegacyTimelineIsAvailable(false);
      setMultipleTimelinesAvailable([]);
      setToast({
        setOpen: setToastOpen,
        open: true,
        location: 'bottom-right',
        time: Date.now(),
        duration: 3000,
        title: 'Timeline Deleted',
        description: 'Timeline deleted successfully.',
        level: ToastLevel.SUCCESS
      });
      // Route to work products index if feature flag is enabled, otherwise route to chat
      if (featureFlags?.workProductNav && caseId) {
        setSelectedView('work-product-index');
        sessionStorage.setItem('view', 'work-product-index');
        // Refresh work products after deletion
        fetchWorkProductsForCase(caseId).then(workProducts => {
          setMultipleTimelinesAvailable(workProducts);
        });
      } else {
        setSelectedView('chat');
      }
      await fetchNewTimeline(undefined, caseId, true);
    } catch (error) {
      console.error('Error Deleting timeline:', error);
      setToast({
        setOpen: setToastOpen,
        open: true,
        location: 'bottom-right',
        time: Date.now(),
        duration: 3000,
        title: 'Timeline Deletion Failed',
        description: 'Timeline deletion failed. Please refresh the page and try again.',
        level: ToastLevel.ERROR
      });
    }
  };
  const handleDeleteTimeline = async () => {
    if (!currentTimelineId) return;
    setAllSlides(undefined);
    setSelectedSecondOption(undefined);
    setLoadingTimeline(true);
    deleteTimeline(currentTimelineId);
  };
  const handlePatchTimeline = async (timelinePatch: TimelinePatch) => {
    if (!currentTimelineId) return;
    patchTimeline(currentTimelineId, timelinePatch);
  };
  const fetchLegacyTimeline = async (documentIdentifier: string) => {
    setTitle(undefined);
    setDescription(undefined);
    setMedia(undefined);
    setIsLegacy(false);
    setAllSlides(undefined);
    setLoadingTimeline(true);
    setNewTimelineIsAvailable(false);
    setLegacyTimelineIsAvailable(false);
    setMultipleTimelinesAvailable([]);
    setLegacyTimelineIsAvailable(false);
    setCurrentTimelineId(undefined);
    try {
      const timelineUrl = `/api/protected/timeline?documentIdentifier=${encodeURIComponent(documentIdentifier)}`;
      const response = await FetchWithVersionCheck(timelineUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'x-tai-id-token': authState?.idToken?.idToken || ''
        }
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (!data || !data.timeline || data.timeline.length === 0) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      setIsLegacy(true);
      setDocIdMode(true);
      setSortByDateTime(true);
      setIsEditableTimeline(false);
      setLoadingTimeline(false);
      setLegacyTimelineIsAvailable(true);
      let displayOrder = 0;
      let newEvents = 0;
      const unsortedSlides = [];
      for (const row of data.timeline) {
        for (const event of row.events) {
          displayOrder += 1;
          newEvents += 1;
          unsortedSlides.push({
            ...omit(row, ['events']),
            ...event,
            show: event.hidden ? false : true,
            order: displayOrder,
            displayOrder: displayOrder,
            approval: event.approved,
            id: event.id ? event.id : `NEW_${newEvents}`,
            date: row.date,
            displayDate: row.date ? row.date : undefined,
            time: event.time,
            slideTitle: event.slideTitle,
            slideSubTitle: event.slideSubTitle,
            slideDescription: event.slideDescription,
            media: event.media ? event.media : event.mediaType && event.mediaSrc ? [{
              mediaType: event.mediaType as MediaType,
              mediaSrc: event.mediaSrc,
              mediaUrl: event.mediaSrc
            }] : [],
            location: stringifyLocation(event.location),
            sources: event.sources ? event.sources : []
          });
        }
      }
      // sort by datetime for legacy timelines
      const sortedSlides = getSortedSlides(unsortedSlides, 'datetime');
      setAllSlides(sortedSlides);
    } catch (error) {
      setAllSlides(undefined);
      setLoadingTimeline(false);
      setLegacyTimelineIsAvailable(false);
      const errorMessage = 'The provided document ID is not valid. Please verify and try again.';
      CaptureError(errorMessage, error as Error, !!featureFlags?.useIntercom);
    }
  };
  const createNewTimeline = async (timelineTitle: string, creatingEventFromChatIn?: boolean) => {
    setTimelineCreated(false);
    try {
      setCreatingTimeline(true);
      const request: any = await FetchWithVersionCheck("/api/protected/timelines", {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'x-tai-id-token': authState?.idToken?.idToken || ''
        },
        body: JSON.stringify({
          action: 'create',
          caseId: caseId,
          title: timelineTitle || caseId,
          description: `New Timeline for ${getCaseDisplayName(caseId)}`,
          tenantId: tenantId
        })
      });
      const response = await request.json();
      setCreatingTimeline(false);
      if (response.timeline && response.timeline.title === (timelineTitle || caseId)) {
        setCurrentTimelineId(response.timeline.id);
        setTimelineCreated(true);
        if (creatingEventFromChatIn && !chatEventToAdd?.autogen) {
          await fetchNewTimeline(response.timeline.id, caseId, false, !!creatingEventFromChatIn);
        } else if (creatingEventFromChatIn && chatEventToAdd?.autogen) {
          // await createEventFromChat(chatEventToAdd, response.timeline.id);
          await fetchNewTimeline(response.timeline.id, caseId, false, !!creatingEventFromChatIn);
        } else {
          await fetchNewTimeline(response.timeline.id, caseId, false, !!creatingEventFromChatIn);
        }
        if (!creatingEventFromChatIn && !chatEventToAdd?.autogen) {
          setToast({
            setOpen: setToastOpen,
            open: true,
            location: 'bottom-right',
            time: Date.now(),
            duration: 5000,
            title: 'Timeline Created',
            description: 'The timeline has been created.',
            level: ToastLevel.SUCCESS,
            actionButtonText: 'Go to Timeline:Details',
            actionButtonOnClick: () => {
              setSelectedView('table');
            }
          });
        }
        return response?.timeline;
      } else {
        setToast({
          setOpen: setToastOpen,
          open: true,
          location: 'bottom-right',
          time: Date.now(),
          duration: 3000,
          title: 'Error',
          description: 'Could not create timeline.',
          level: ToastLevel.ERROR
        });
      }
      return response;
    } catch (error) {
      setToast({
        setOpen: setToastOpen,
        open: true,
        location: 'bottom-right',
        time: Date.now(),
        duration: 3000,
        title: 'Error',
        description: 'Could not create timeline.',
        level: ToastLevel.ERROR
      });
      return undefined;
    }
  };
  const updateEvent = async (updatedEvent: Slide, shouldRefetchTimeline: boolean = true) => {
    if (!currentTimelineId) return;
    setLoadingTimeline(shouldRefetchTimeline);
    try {
      const request: any = await FetchWithVersionCheck("/api/protected/events", {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'x-tai-id-token': authState?.idToken?.idToken || ''
        },
        body: JSON.stringify({
          event: updatedEvent,
          docId: currentTimelineId,
          tenantId: tenantId
        })
      });
      const response = await request.json();
      if (request.status === 200 && response.response === 200) {
        if (shouldRefetchTimeline) {
          await fetchNewTimeline(currentTimelineId, caseId, true);
        } else {
          setLoadingTimeline(false);
        }
      } else {
        setToast({
          setOpen: setToastOpen,
          open: true,
          location: 'bottom-right',
          time: Date.now(),
          duration: 3000,
          title: 'Event Update Failed',
          description: 'Event update failed. Please try again.',
          level: ToastLevel.ERROR
        });
      }
    } catch (error) {
      setLoadingTimeline(false);
      const errorMessage = 'Could not update event.';
      CaptureError(errorMessage, error as Error, !!featureFlags?.useIntercom);
    }
  };
  const createEvent = async (newEvent: Slide, timelineId: string | null, shouldRefetchTimeline: boolean = true) => {
    if (!currentTimelineId && !timelineId) return;
    setLoadingTimeline(true);
    const timelineIdToUse = currentTimelineId || timelineId;
    newEvent.sources = newEvent.sources ? newEvent.sources.map((source: SourceObject) => {
      return {
        ...source,
        sourceType: Array.isArray(source.sourceType) ? source.sourceType[0] : source.sourceType
      };
    }) : undefined;
    newEvent.media = newEvent.media ? newEvent.media.map((media: MediaObject) => {
      return {
        ...media,
        mediaType: Array.isArray(media.mediaType) ? media.mediaType[0] : media.mediaType
      };
    }) : undefined;
    try {
      const request: any = await FetchWithVersionCheck("/api/protected/events", {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'x-tai-id-token': authState?.idToken?.idToken || ''
        },
        body: JSON.stringify({
          event: newEvent,
          docId: timelineIdToUse,
          tenantId: tenantId
        })
      });
      const response = await request.json();
      if (request.status === 200 && response.response === 201) {
        setToast({
          setOpen: setToastOpen,
          open: true,
          location: 'bottom-right',
          time: Date.now(),
          duration: 3000,
          title: 'Event Created',
          description: 'Event was successfully created.',
          level: ToastLevel.SUCCESS
        });
        if (shouldRefetchTimeline) {
          const gettingTimeline = await fetchNewTimeline(timelineIdToUse || undefined, caseId, true);
          // setSelectedView('table'); // disabled for now, so that we don't navigate away from the chat
        }
      } else {
        setToast({
          setOpen: setToastOpen,
          open: true,
          location: 'bottom-right',
          time: Date.now(),
          duration: 3000,
          title: 'Event Creation Failed',
          description: 'Event creation failed. Please try again.',
          level: ToastLevel.ERROR
        });
      }
    } catch (error) {
      setLoadingTimeline(false);
      setToast({
        setOpen: setToastOpen,
        open: true,
        location: 'bottom-right',
        time: Date.now(),
        duration: 3000,
        title: 'Event Creation Failed',
        description: 'Event creation failed. Please try again.',
        level: ToastLevel.ERROR
      });
      const errorMessage = 'Could not create event.';
      CaptureError(errorMessage, error as Error, !!featureFlags?.useIntercom);
    }
  };
  useEffect(() => {
    const modelArnsListFromFeatureFlag = featureFlags?.['modelArnsList'];
    if (modelArnsListFromFeatureFlag) {
      setModelArnsList(modelArnsListFromFeatureFlag);
    } else if (featureFlags?.['systemStage']) {
      const systemStage = featureFlags?.['systemStage'] as string;
      const modelArnsListHardcoded = MODEL_ARNS_LIST(systemStage);
      setModelArnsList(modelArnsListHardcoded);
    } else {
      const modelArnsListHardcoded = MODEL_ARNS_LIST('prod');
      setModelArnsList(modelArnsListHardcoded);
    }

    // Refusal Messages from the llmConfig feature flag take precedence, so check both
    // this code is currently duplicated in answer-item.tsx
    const refusalMessagesFromFeatureFlag: any = featureFlags?.['refusalMessages'];
    const passMessagesFromFeatureFlag: any = featureFlags?.['passMessages'];
    const llmConfigFromFeatureFlag = featureFlags?.['llmConfig'];
    const llmConfigRefusalMessages = (llmConfigFromFeatureFlag as unknown as LLMConfig)?.refusalMessages;
    const llmConfigPassMessages = (llmConfigFromFeatureFlag as unknown as LLMConfig)?.passMessages;
    const refusalMessagesArray = refusalMessagesFromFeatureFlag?.refusalMessages && Array.isArray(refusalMessagesFromFeatureFlag.refusalMessages) ? refusalMessagesFromFeatureFlag.refusalMessages : [];
    const passMessagesArray = passMessagesFromFeatureFlag?.passMessages && Array.isArray(passMessagesFromFeatureFlag.passMessages) ? passMessagesFromFeatureFlag.passMessages : [];
    const llmConfigRefusalMessagesArray = Array.isArray(llmConfigRefusalMessages) ? llmConfigRefusalMessages : [];
    const llmConfigPassMessagesArray = Array.isArray(llmConfigPassMessages) ? llmConfigPassMessages : [];
    if (llmConfigRefusalMessagesArray.length) {
      setRefusalMessages(llmConfigRefusalMessagesArray);
    } else if (refusalMessagesArray.length) {
      setRefusalMessages(refusalMessagesArray);
    } else {
      setRefusalMessages(REFUSAL_MESSAGES);
    }
    if (llmConfigPassMessagesArray.length) {
      setPassMessages(llmConfigPassMessagesArray);
    } else if (passMessagesArray.length) {
      setPassMessages(passMessagesArray);
    } else {
      setPassMessages(PASS_MESSAGES);
    }

    // LLM Config from the llmConfig feature flag takes precedence and overrides the refusalMessages flag too
    if (llmConfigFromFeatureFlag) {
      const lDllmConfig: LLMConfig = llmConfigFromFeatureFlag as unknown as LLMConfig;
      if (lDllmConfig.refusalMessages !== undefined && lDllmConfig.passMessages !== undefined && lDllmConfig.topP !== undefined && lDllmConfig.textResponsePartDelimiter !== undefined && lDllmConfig.overheadPerPair !== undefined && lDllmConfig.markdownDirective !== undefined && lDllmConfig.promptTemplate !== undefined && lDllmConfig.conversationContext !== undefined && lDllmConfig.promptTemplateWithContext !== undefined && lDllmConfig.orchestrationPromptTemplate !== undefined && lDllmConfig.historyWordLimit !== undefined && lDllmConfig.historyCharLimit !== undefined && lDllmConfig.temperature !== undefined && lDllmConfig.unalignedTemperature !== undefined && lDllmConfig.maxTokens !== undefined && lDllmConfig.numOfResults !== undefined && lDllmConfig.overrideSearchType !== undefined && lDllmConfig.supportedQueryTransformationConfigTypes !== undefined) {
        setLlmConfig(lDllmConfig as unknown as LLMConfig);
      }
    }
  }, [featureFlags]);
  const enrichEvent = async (formData: Slide) => {
    let query = '';
    if (featureFlags?.enrichEventPrompt && (featureFlags?.enrichEventPrompt as string).trim() !== '') {
      const title = formData?.slideTitle || undefined;
      const description = formData?.slideDescription || '';
      query = featureFlags?.enrichEventPrompt as string;
      if (title && title.trim() !== '') {
        query = query.replace(/<titlePrompt>([\S\s]*?)<\/titlePrompt>/g, '$1');
        query = query.replace('{title}', title);
      } else {
        query = query.replace(/<titlePrompt>([\S\s]*?)<\/titlePrompt>/g, '');
      }
      if (description && description.trim() !== '') {
        query = query.replace(/<descriptionPrompt>([\S\s]*?)<\/descriptionPrompt>/g, '$1');
        query = query.replace('{description}', description);
      } else {
        query = query.replace(/<descriptionPrompt>([\S\s]*?)<\/descriptionPrompt>/g, '');
      }
    } else {
      query = 'Tell me more about';
      let useAnd = false;
      if (formData?.slideTitle && formData?.slideTitle.trim() !== '') {
        query += ` "${formData?.slideTitle} as it relates to this case"`;
        useAnd = true;
      }
      if (formData?.slideDescription && formData?.slideDescription.trim() !== '') {
        query += `${useAnd ? ' and' : ''} "${formData?.slideDescription} as it relates to this case"`;
        useAnd = true;
      }
    }
    try {
      const chatPath = '/api/protected/chat-service-proxy';
      const idTokenInfo = getTokenInfo(authState?.idToken?.idToken || '');
      const accessTokenInfo = getTokenInfo(accessToken || '');
      const userId = getUserEmailForHeaders(idTokenInfo, accessTokenInfo);
      let body: any = {
        enrichEvent: true,
        deepDive: false,
        threatReport: false,
        query: query,
        selectedCase: caseId,
        caseId: caseId,
        username: getUsername(idTokenInfo),
        files: [],
        scopes: [],
        excludedFiles: [],
        excludedScopes: [],
        userId: userId,
        sessionId: null,
        retryCount: 0,
        refusalMessages: refusalMessages,
        passMessages: passMessages,
        modelArnsList: modelArnsList,
        llmConfig: llmConfig,
        useQueryDecomposition: false,
        useOpenSearch: !!featureFlags?.['useOpenSearchForEnrich'] || false,
        showPreliminaryResult: false,
        useNewTimeSaved: false
      };
      const pollHeaders = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken || ''}`,
        'x-tai-id-token': authState?.idToken?.idToken || ''
      };
      let rawResult = await FetchWithVersionCheck(chatPath, {
        method: 'POST',
        headers: pollHeaders,
        body: JSON.stringify(body)
      });
      let result = await rawResult.json();
      body = {
        enrichEvent: result?.enrich_event ?? false,
        sessionId: result?.sessionId,
        epochTimeSecs: result?.epoch_time_secs,
        chatKey: result?.chat_key,
        requestType: result?.request_type
      };
      const startTime = Date.now();
      while (isEnrichmentHistoryPending(result?.status) && Date.now() - startTime < ENRICHMENT_POLL_TIMEOUT_MS) {
        await new Promise(resolve => setTimeout(resolve, ENRICHMENT_POLL_INTERVAL_MS));
        rawResult = await FetchWithVersionCheck('/api/protected/chat/history', {
          method: 'POST',
          headers: pollHeaders,
          body: JSON.stringify(body)
        });
        result = await rawResult.json();
      }
      if (result?.status === 'error') {
        return formData as Slide;
      }
      const answer = JSON.parse(result?.answer);
      const enrichment = computeEnrichmentFromApiResponse(answer);
      if (enrichment) {
        const enrichedSources = enrichment.sources ?? [];
        const enrichedMedia = enrichment.media ?? [];
        const newSources = dedupeSources([...enrichedSources]);
        const newMedia = dedupeMediaSources([...enrichedMedia]);
        const currentEvent = formData;
        const newFormData = {
          id: currentEvent?.id || null,
          displayOrder: currentEvent?.displayOrder || -1,
          date: currentEvent?.date ? currentEvent.date : null,
          displayDate: currentEvent?.displayDate ? currentEvent.displayDate : currentEvent?.date || null,
          time: currentEvent?.time ? currentEvent.time : null,
          isTimeValid: currentEvent?.isTimeValid === true ? true : false,
          eventLocalTimezone: currentEvent?.eventLocalTimezone || null,
          slideTitle: currentEvent?.slideTitle || '',
          slideSubTitle: currentEvent?.slideSubTitle || undefined,
          slideDescription: enrichment.slideDescription || currentEvent?.slideDescription || '',
          eventType: currentEvent?.eventType || '',
          sources: newSources,
          media: newMedia,
          show: currentEvent?.show || false
        };
        return newFormData as Slide;
      }
      return formData as Slide;
    } catch {
      return formData as Slide;
    }
  };
  const createEventsFromParsedEvents = async (parsedEvents: Slide[], enriching: boolean, edit: boolean, timelineIdIn?: string) => {
    setParsedEventsFromChat(undefined);
    if (!caseId || caseId.trim() === '') {
      return;
    }
    if (!parsedEvents || parsedEvents.length === 0) {
      setToast({
        setOpen: setToastOpen,
        open: true,
        location: 'bottom-right',
        time: Date.now(),
        duration: 5000,
        title: 'The text could not be reliably parsed',
        description: 'Try asking a question like this: "Provide a detailed timeline using all available information.  Use numeric month/day format for dates."',
        level: ToastLevel.INFO
      });
      return;
    }
    let timelineId = timelineIdIn || currentTimelineId || null;
    if (!timelineId && parsedEvents.length > 0) {
      // force timeline selection
      setUseEnrichment(enriching);
      setParsedEventsFromChat(parsedEvents);
      setPleaseSelectTimeline(true);
      return;
    }
    setUseEnrichment(false);
    setLoadingTimeline(true);
    try {
      const sortedParsedEvents = edit ? parsedEvents : parsedEvents.sort((a, b) => {
        if (!a.date) {
          return -1;
        }
        if (!b.date) {
          return 1;
        }
        const aDate = new Date(a.date);
        const bDate = new Date(b.date);
        return aDate.getTime() - bDate.getTime();
      });
      const count = sortedParsedEvents.length;
      const timer = enriching ? 120000 : 10000;
      setToast({
        setOpen: setToastOpen,
        open: true,
        location: 'bottom-right',
        time: Date.now(),
        duration: count * timer,
        title: `${edit ? 'Enriching' : 'Creating'} Event(s)`,
        description: `${edit ? 'Enriching' : 'Creating'} event(s), this may take a minute...`,
        level: ToastLevel.INFO,
        child: <div className="flex flex-col gap-2 items-center justify-center">
            <Spinner size="sm" color="primary" />
          </div>
      });
      for (let i = 0; i < count; i++) {
        setLoadingTimeline(true);
        let newEvent: Slide = {
          ...sortedParsedEvents[i],
          show: sortedParsedEvents[i].show !== undefined ? sortedParsedEvents[i].show : true
        };
        if (!edit) {
          if (sortedParsedEvents[i].slideTitle) {
            newEvent.slideTitle = sortedParsedEvents[i].slideTitle;
          }
          if (sortedParsedEvents[i].slideDescription) {
            newEvent.slideDescription = sortedParsedEvents[i].slideDescription;
          }
          if (sortedParsedEvents[i].date) {
            newEvent.date = sortedParsedEvents[i].date;
          }
        }
        if ((featureFlags?.['autoEnrichEvents'] || edit) && enriching) {
          const enrichedEvent = await enrichEvent(newEvent);
          if (enrichedEvent) {
            if (edit) {
              await updateEvent(enrichedEvent as Slide, false);
            } else {
              await createEvent(enrichedEvent as Slide, timelineId, false);
            }
          }
        } else {
          await createEvent(newEvent, timelineId, false);
        }
        setLoadingTimeline(true);
        setToast({
          setOpen: setToastOpen,
          open: true,
          location: 'bottom-right',
          time: Date.now(),
          duration: i === count - 1 ? 5000 : (i + 1) * timer,
          title: `${edit ? 'Enriched' : 'Created'} Event ${i + 1} of ${count}`,
          description: `${edit ? 'Enriched' : 'Created'} event ${i + 1} of ${count}, waiting for the rest...`,
          level: ToastLevel.SUCCESS,
          child: <div className="flex flex-col gap-2 items-center justify-center">
              <Progress value={(i + 1) / count * 100} size="lg" color="success" className="box-border w-[calc(100%-20px)] mx-[10px] h-[30px]" />
              <div className="text-md text-gray-800">
                {`${edit ? 'Enriched' : 'Created'} event ${i + 1} of ${count}`}
              </div>
            </div>
        });
      }
      try {
        if (entityJobId) {
          await getEventsFromEntityService('DELETE_JOB', timelineId || null, caseId || '', '', [], 0, entityJobId);
        }
      } catch (error) {
        // ignore
      }
      await fetchNewTimeline(timelineId || undefined, caseId, true);
      setToast({
        setOpen: setToastOpen,
        open: true,
        location: 'bottom-right',
        time: Date.now(),
        duration: 5000,
        title: `${edit ? 'Enriched' : 'Created'} Event(s)`,
        description: `${edit ? 'Enriched' : 'Created'} event(s) successfully.`,
        actionButtonText: selectedView === 'chat' ? 'Go to Timeline:Details' : 'Go to Timeline:Table',
        actionButtonOnClick: selectedView === 'chat' ? () => {
          setSelectedView('table');
        } : undefined,
        level: ToastLevel.SUCCESS
      });
    } catch (error) {
      setLoadingTimeline(false);
      setToast({
        setOpen: setToastOpen,
        open: true,
        location: 'bottom-right',
        time: Date.now(),
        duration: 3000,
        title: `Event ${edit ? 'Enrichment' : 'Creation'} Failed`,
        description: `Event ${edit ? 'Enrichment' : 'Creation'} failed. Please try again.`,
        level: ToastLevel.ERROR
      });
    }
  };
  const deleteEvent = async (eventToDelete: Slide, shouldRefetchTimeline: boolean = true): Promise<boolean> => {
    if (!currentTimelineId) return false;
    setLoadingTimeline(true);
    try {
      const request: any = await FetchWithVersionCheck("/api/protected/events", {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'x-tai-id-token': authState?.idToken?.idToken || ''
        },
        body: JSON.stringify({
          event: eventToDelete,
          docId: currentTimelineId,
          tenantId: tenantId
        })
      });
      const response = await request.json();
      if (request.status === 200 && response.response === 204) {
        if (shouldRefetchTimeline) {
          await fetchNewTimeline(currentTimelineId, caseId, false);
        }
        return true;
      }
      return false;
    } catch (error) {
      setLoadingTimeline(false);
      const errorMessage = 'Could not delete event.';
      CaptureError(errorMessage, error as Error, !!featureFlags?.useIntercom);
      return false;
    }
  };

  // poll specific job status waiting for the job to complete and apply the results to the timeline
  useEffect(() => {
    if (entityServiceJob.count > 0 && entityServiceJob.count < 50 && !!caseId && entityServiceJob.caseId === caseId && !!entityServiceJob.jobId && entityServiceJob.jobId.trim() !== '') {
      if (entityServiceJobPollRef.current) {
        clearTimeout(entityServiceJobPollRef.current);
        entityServiceJobPollRef.current = null;
      }
      entityServiceJobPollRef.current = setTimeout(() => {
        setEntityServiceJob({
          ...entityServiceJob,
          count: entityServiceJob.count + 1
        });
        getEventsFromEntityService('GET_JOB_STATUS', currentTimelineId || null, caseId || '', '', [], 0, entityServiceJob.jobId || '');
      }, 5000);
    } else if (entityServiceJob.caseId !== undefined && entityServiceJob.caseId !== undefined) {
      if (entityServiceJobPollRef.current) {
        clearTimeout(entityServiceJobPollRef.current);
        entityServiceJobPollRef.current = null;
      }
      setEntityServiceJob({
        jobId: undefined,
        caseId: undefined,
        count: 0
      });
    }
  }, [entityServiceJob.jobId, entityServiceJob.caseId, entityServiceJob.count, caseId]);

  // poll all jobs waiting for the job to complete so they can be used in the table view
  useEffect(() => {
    if (entityServicePollAllJobsCount > 0 && entityServicePollAllJobsCount < 50 && !!caseId && caseId.trim() !== '') {
      setEntityServiceJob({
        jobId: undefined,
        caseId: undefined,
        count: 0
      });
      // cancel any single job polling
      if (entityServiceJobPollRef.current) {
        clearTimeout(entityServiceJobPollRef.current);
        entityServiceJobPollRef.current = null;
      }
      if (entityServicePollAllJobsRef.current) {
        clearTimeout(entityServicePollAllJobsRef.current);
        entityServicePollAllJobsRef.current = null;
      }
      entityServicePollAllJobsRef.current = setTimeout(() => {
        setEntityServicePollAllJobsCount(entityServicePollAllJobsCount + 1);
        getEventsFromEntityService('GET_ALL_JOBS', currentTimelineId || null, caseId || '', '', [], 0, null);
      }, 5000);
    } else {
      if (entityServicePollAllJobsRef.current) {
        clearTimeout(entityServicePollAllJobsRef.current);
        entityServicePollAllJobsRef.current = null;
      }
      setEntityServicePollAllJobsCount(0);
    }
  }, [caseId, entityServicePollAllJobsCount]);
  const handleCancelPleaseSelectTimeline = () => {
    setPleaseSelectTimeline(false);
    setParsedEventsFromChat(undefined);
    setChatEventToAdd(undefined);
    setModalSelectedTimeline(undefined);
    setIsEditableTimeline(false);
  };
  const createEventsFromEntityEvents = async (jobId: string, events: any[], enriching: boolean, timelineIdIn?: string) => {
    if (!caseId || caseId.trim() === '') {
      return;
    }
    if ((!timelineIdIn || timelineIdIn.trim() === '') && (!currentTimelineId || currentTimelineId.trim() === '')) {
      setUseEnrichment(enriching);
      const newEvents = entityEventsToSlides(events, allSourcesFromChat?.allSources || [], allSourcesFromChat);
      setParsedEventsFromChat(newEvents);
      setEntityJobId(jobId);
      setPleaseSelectTimeline(true);
      return;
    }
    const newEvents = entityEventsToSlides(events, allSourcesFromChat?.allSources || [], allSourcesFromChat);
    await createEventsFromParsedEvents(newEvents, enriching, false, timelineIdIn || currentTimelineId);
    await getEventsFromEntityService('DELETE_JOB', timelineIdIn || null, caseId || '', '', [], 0, jobId);
  };
  const getEventsFromEntityService = async (action: string, timelineId: string | null, caseId: string, answerItemText: string, sources: Source[], epochTimeSecs: number, jobId: string | null) => {
    if (!accessToken) return;
    if (action !== 'GET_ALL_JOBS' && action !== 'DELETE_JOB') {
      if (!timelineId) return;
      setLoadingTimeline(true);
    }
    if (action !== 'DELETE_JOB' && action !== 'GET_JOB_STATUS' && action !== 'GET_ALL_JOBS') {
      setToast({
        setOpen: setToastOpen,
        open: true,
        location: 'bottom-right',
        time: Date.now(),
        duration: 602000,
        title: 'Extracting Event(s) from Chat Answer',
        description: 'Extracting event(s) from chat answer, this may take several minutes...',
        level: ToastLevel.INFO,
        child: <div className="flex flex-col gap-2 items-center justify-center">
            <Spinner size="sm" color="primary" />
          </div>
      });
    }
    try {
      const postBody: {
        action?: string;
        text?: string;
        tags?: string[];
        sources?: Source[];
        epochTimeSecs?: number;
        jobId?: string;
      } = {};
      if (action === 'CREATE_JOB') {
        postBody.tags = [`CASE:${caseId}`];
        postBody.sources = [];
        postBody.text = answerItemText;
        postBody.epochTimeSecs = epochTimeSecs;
      } else if (action === 'GET_JOB_STATUS' && jobId) {
        postBody.jobId = jobId;
      } else if (action === 'GET_ALL_JOBS') {
        postBody.tags = [`CASE:${caseId}`];
      } else if (action === 'DELETE_JOB' && jobId) {
        setJobsBeingDeleted([...jobsBeingDeleted, jobId]);
        postBody.jobId = jobId;
      } else if (action === 'GET_EVENTS') {
        postBody.text = answerItemText;
        postBody.sources = sources;
      } else {
        throw new Error('Invalid Entity Service action from UI', {
          cause: {
            action
          }
        });
      }
      postBody.action = action;
      if (tenantId) {
        (postBody as any).tenantId = tenantId;
      }
      const request: any = await FetchWithVersionCheck("/api/protected/entity", {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'x-tai-id-token': authState?.idToken?.idToken || ''
        },
        body: JSON.stringify(postBody),
        signal: AbortSignal.timeout(602000)
      });
      if (action === 'DELETE_JOB') {
        // get all jobs after deleting a job to update the list of jobs
        await getEventsFromEntityService('GET_ALL_JOBS', timelineId || null, caseId || '', '', [], 0, null);
        setJobsBeingDeleted([...jobsBeingDeleted.filter((job: any) => job.jobId !== jobId)]);
        return;
      } else if (action === 'GET_ALL_JOBS') {
        setJobsBeingDeleted([]);
        const allJobsResponse = await request.json();
        const entityExtractionJobs = allJobsResponse.response && allJobsResponse.response.length > 0 ? allJobsResponse.response : [];
        if (entityExtractionJobs.length === 0) {
          setEntityServicePollAllJobsCount(0);
        } else {
          const somePending = entityExtractionJobs.some((job: any) => job.status === 'pending');
          if (!somePending) {
            setEntityServicePollAllJobsCount(0);
          }
        }
        setEntityExtractionJobs(entityExtractionJobs);
        return;
      }
      const entityResponse = await request.json();
      const response = entityResponse.jobId ? entityResponse : entityResponse.response;
      let eventsToParse: Slide[] = [];
      let processEvents = false;
      if (action === 'CREATE_JOB' && epochTimeSecs && response && response.jobId) {
        setEntityExtractionJobs([...entityExtractionJobs, {
          jobId: response.jobId,
          status: 'pending',
          result: {
            events: []
          },
          epochTimeSecs: response.epochTimeSecs
        }]);
        // use the jobId in the response to poll for the events
        setEntityServiceJob({
          jobId: response.jobId,
          caseId: caseId,
          count: 1
        });
        setLoadingTimeline(false);
        setToast({
          setOpen: setToastOpen,
          open: true,
          location: 'bottom-right',
          time: Date.now(),
          duration: 602000,
          title: 'Event Extraction Job Started',
          description: `The event extraction job has started. Please do not change the case or timeline. You will be notified when it is complete.`,
          level: ToastLevel.INFO,
          child: <div className="flex flex-col gap-2 items-center justify-center">
              <Spinner size="sm" color="primary" />
            </div>
        });
        return;
      } else if (action === 'GET_JOB_STATUS' && jobId && response && (response.status === 'completed' || response.status === 'failed' || response.status === 'deleted')) {
        // cancel any single job polling
        if (entityServiceJobPollRef.current) {
          clearTimeout(entityServiceJobPollRef.current);
          entityServiceJobPollRef.current = null;
        }
        // response from polling, use the events for the correct answer
        setEntityServiceJob({
          jobId: undefined,
          caseId: undefined,
          count: 0
        });
        // parse the events from the response
        setEntityExtractionJobs([...entityExtractionJobs, response]);
        if (response.status === 'completed') {
          processEvents = true;
          eventsToParse = response.result?.events || [];
        }
        getEventsFromEntityService('DELETE_JOB', timelineId, caseId, answerItemText, sources, epochTimeSecs, response.jobId);
      } else if (action === 'GET_JOB_STATUS' && response && response.status === 'pending') {
        setEntityExtractionJobs([...entityExtractionJobs, response]);
        setToast({
          setOpen: setToastOpen,
          open: true,
          location: 'bottom-right',
          time: Date.now(),
          duration: 602000,
          title: 'Event Extraction Job Pending',
          description: `The event extraction job is still running. Please do not change the case or timeline. You will be notified when it is complete.`,
          level: ToastLevel.INFO,
          child: <div className="flex flex-col gap-2 items-center justify-center">
              <Spinner size="sm" color="primary" />
            </div>,
          actionButtonText: 'Notify me upon completion',
          actionButtonOnClick: () => {
            setLoadingTimeline(false);
            // cancel any single job polling
            if (entityServiceJobPollRef.current) {
              clearTimeout(entityServiceJobPollRef.current);
              entityServiceJobPollRef.current = null;
            }
            setEntityServiceJob({
              jobId: undefined,
              caseId: undefined,
              count: 0
            });
            // kick off polling for all jobs to check for old jobs
            setEntityServicePollAllJobsCount(1);
            setToast({
              setOpen: setToastOpen,
              open: true,
              location: 'bottom-right',
              time: Date.now(),
              duration: 5000,
              title: 'Polling Stopped',
              description: 'You can get the results in the Details View in several minutes.',
              level: ToastLevel.INFO
            });
          }
        });
        return;
      } else if (action === 'GET_JOB_STATUS' && response && response.status === 'failed') {
        // cancel any single job polling
        if (entityServiceJobPollRef.current) {
          clearTimeout(entityServiceJobPollRef.current);
          entityServiceJobPollRef.current = null;
        }
        setEntityServiceJob({
          jobId: undefined,
          caseId: undefined,
          count: 0
        });
        setEntityExtractionJobs([...entityExtractionJobs, response]);
        setLoadingTimeline(false);
        setToast({
          setOpen: setToastOpen,
          open: true,
          location: 'bottom-right',
          time: Date.now(),
          duration: 5000,
          title: 'Event Extraction Job Failed',
          description: `The event extraction job failed. Please try again.`,
          level: ToastLevel.ERROR
        });
        return;
      } else if (response && response.status && response.status !== 'completed' && response.status !== 'failed' && response.status !== 'deleted' && response.status !== 'pending') {
        // unknown status, do nothing
        // cancel any single job polling
        if (entityServiceJobPollRef.current) {
          clearTimeout(entityServiceJobPollRef.current);
          entityServiceJobPollRef.current = null;
        }
        setEntityExtractionJobs([...entityExtractionJobs, response]);
        setEntityServiceJob({
          jobId: undefined,
          caseId: undefined,
          count: 0
        });
      }
      if (processEvents && eventsToParse && eventsToParse.length > 0) {
        const count = eventsToParse.length;
        setLoadingTimeline(true);
        setToast({
          setOpen: setToastOpen,
          open: true,
          location: 'bottom-right',
          time: Date.now(),
          duration: 602000,
          title: 'Event(s) Extracted',
          description: `Extracted ${count} event(s) successfully. Please do not change the case or timeline. Adding to timeline...`,
          level: ToastLevel.SUCCESS
        });
        const newEvents = entityEventsToSlides(eventsToParse, sources, allSourcesFromChat).sort((a: Slide, b: Slide) => {
          if (!a.date) {
            return -1;
          }
          if (!b.date) {
            return 1;
          }
          const aDate = new Date(a.date);
          const bDate = new Date(b.date);
          return aDate.getTime() - bDate.getTime();
        });
        // create the events
        const timer = 10000; // change this if enriching

        for (let i = 0; i < count; i++) {
          await createEvent(newEvents[i], timelineId, false);
          setLoadingTimeline(true);
          if (i < count - 1) {
            setToast({
              setOpen: setToastOpen,
              open: true,
              location: 'bottom-right',
              time: Date.now(),
              duration: i === count - 1 ? 5000 : (i + 1) * timer,
              title: `'Created Event ${i + 1} of ${count}`,
              description: `Created event ${i + 1} of ${count}, waiting for the rest. Please do not change the case or timeline.`,
              level: ToastLevel.INFO,
              child: <div className="flex flex-col gap-2 items-center justify-center">
                  <Progress value={(i + 1) / count * 100} size="lg" color="success" className="box-border w-[calc(100%-20px)] mx-[10px] h-[30px]" />
                  <div className="text-md text-gray-800">
                    {`Created event ${i + 1} of ${count}`}
                  </div>
                </div>
            });
          } else {
            setToast({
              setOpen: setToastOpen,
              open: true,
              location: 'bottom-right',
              time: Date.now(),
              duration: i === count - 1 ? 5000 : (i + 1) * timer,
              title: `'Created Event ${i + 1} of ${count}`,
              description: `Created event ${i + 1} of ${count}, waiting for the rest. Please do not change the case or timeline.`,
              level: ToastLevel.INFO,
              actionButtonText: selectedView === 'chat' ? 'Go to Timeline:Details' : 'Go to Timeline:Table',
              actionButtonOnClick: selectedView === 'chat' ? () => {
                setSelectedView('table');
              } : undefined,
              child: <div className="flex flex-col gap-2 items-center justify-center">
                  <Progress value={(i + 1) / count * 100} size="lg" color="success" className="box-border w-[calc(100%-20px)] mx-[10px] h-[30px]" />
                  <div className="text-md text-gray-800">
                    {`Created event ${i + 1} of ${count}`}
                  </div>
                </div>
            });
          }
        }
        if (timelineId) {
          await fetchNewTimeline(timelineId, caseId, true);
        }
      } else if (action === 'GET_JOB_STATUS' && response.status === 'completed') {
        // cancel any single job polling
        if (entityServiceJobPollRef.current) {
          clearTimeout(entityServiceJobPollRef.current);
          entityServiceJobPollRef.current = null;
        }
        setEntityServiceJob({
          jobId: undefined,
          caseId: undefined,
          count: 0
        });
        setLoadingTimeline(false);
        setToast({
          setOpen: setToastOpen,
          open: true,
          location: 'bottom-right',
          time: Date.now(),
          duration: 5000,
          title: 'No Event(s) could be generated',
          description: 'Event(s) creation failed. No events could be generated from the provided chat answer.',
          level: ToastLevel.INFO
        });
      }
    } catch (error) {
      setEntityServiceJob({
        jobId: undefined,
        caseId: undefined,
        count: 0
      });
      setEntityServicePollAllJobsCount(0);
      setLoadingTimeline(false);
    }
  };
  const deleteEntityExtractionJob = async (jobId: string) => {
    getEventsFromEntityService('DELETE_JOB', null, '', '', [], 0, jobId);
  };

  // addToTimeline prop for chat/answerItem
  const createEventFromChat = async (chatEventToAddIn: EventFromChat) => {
    if (!caseId || caseId.trim() === '') {
      return;
    }
    setChatEventToAdd(chatEventToAddIn);
    // Pre-select current timeline if available
    setModalSelectedTimeline(currentTimelineId);
    // Always show confirmation modal for chat-originated events
    setPleaseSelectTimeline(true);
  };

  // Called after user confirms timeline selection in the modal
  const proceedWithChatEvent = async (timelineId: string) => {
    const chatEventToUse = chatEventToAdd;
    if (chatEventToUse) {
      if (chatEventToUse.autogen && chatEventToUse.answerText && caseId && caseId.trim() !== '') {
        const action = 'CREATE_JOB';
        const createdEvents = await getEventsFromEntityService(action, timelineId, caseId, chatEventToUse.answerText, chatEventToUse.sources, chatEventToUse.epochTimeSecs, null);
      } else if (chatEventToUse?.answerText === 'FROM_SOURCE' && caseId && caseId.trim() !== '') {
        const newEvent = {
          id: 'NEW_EVENT_FROM_CHAT',
          slideTitle: 'FROM SOURCE',
          slideDescription: chatEventToUse.slideDescription,
          show: true,
          approval: 'approved',
          sources: chatEventToUse.sources as SourceObject[]
        };
        openModalFromTable({
          ...newEvent
        }, 'newEventFromChat');
      } else if (chatEventToUse.answerText && caseId && caseId.trim() !== '') {
        const description = getDescriptionFromAnswerText(chatEventToUse.answerText);
        const sourcesFromChatEvent = chatEventToUse.sources.map((source: Source) => {
          return {
            sourceType: extractPathAfterBulk(source.sourceFilePath || '').sourceType || 'Unknown',
            sourceUrl: source.sourceFilePath,
            sourceContext: source.sourceContext
          };
        });
        const sourcesForEvents = dedupeSources(sourcesFromChatEvent);
        // TODO: only id is currently used, modal rebuilds this event
        const newEvent = {
          id: 'NEW_EVENT_FROM_CHAT',
          slideTitle: '',
          slideDescription: description,
          show: true,
          approval: 'approved',
          sources: sourcesForEvents
        };
        openModalFromTable({
          ...newEvent
        }, 'newEventFromChat');
      }
    }
  };
  const handleAddEvent = async (newEvent: Slide) => {
    if (!currentTimelineId) return;
    setLoadingTimeline(true);
    const createdEvent = await createEvent(newEvent, currentTimelineId, true);
  };
  const handleDeleteEvent = async (event: Slide) => {
    if (allSlides) {
      setLoadingTimeline(true);
      deleteEvent(event);
    }
  };
  useEffect(() => {
    const resetForNewCase = async () => {
      setDocIdMode(false);
      setTimelineTags([]);
      setIsFullscreen(false);
      setIsEventModalOpen(false);
      setNewTimelineIsAvailable(false);
      setLegacyTimelineIsAvailable(false);
      setAllSlides(undefined);
      setEntityExtractionJobs([]);
      setChatHistorySessions(null);
      setEntityServiceJob({
        jobId: undefined,
        caseId: undefined,
        count: 0
      });
      setEntityServicePollAllJobsCount(0);
      setThreatReportSources({
        fiveCastFiles: [],
        magnetFiles: []
      });
      setSelectedFivecastSource(undefined);
      setSelectedMagnetSource(undefined);
      if (currentTimelineId && currentTimelineId.trim() !== '' || caseId && caseId.trim() !== '') {
        await fetchNewTimeline(undefined, caseId, false);
      } else if (featureFlags?.legacyTimelines && query && query.trim() !== '') {
        await fetchLegacyTimeline(query);
      }
    };
    resetForNewCase();
    setEntityServicePollAllJobsCount(1); // kick off polling for all jobs to check for old jobs
  }, [caseId, query]);
  const handleSetCaseId = (option: string | undefined) => {
    setQuery('');
    setMultipleTimelinesAvailable([]);
    setSelectedSecondOption(undefined);
    setCaseId(option);
    if (option && option.trim() !== '') {
      getEventsFromEntityService('GET_ALL_JOBS', null, option || '', '', [], 0, '');
    }
  };
  const handleSetSelectedSecondOptionFromDelete = (option: string | undefined) => {
    setMultipleTimelinesAvailable([]);
    setSelectedSecondOption(option);
  };
  useEffect(() => {
    if (featureFlags['timeSaved'] || featureFlags['newTimeSaved']) {
      const timeSavedLocalFromStorage = localStorage.getItem(`timeSaved-${featureFlags?.orgId}`);
      if (timeSavedLocalFromStorage && Number(timeSavedLocalFromStorage) > THRESHOLD_TO_SHOW_LOCAL_TIME_SAVED) {
        setTimeSavedLocal(parseInt(timeSavedLocalFromStorage));
      }
    } else {
      setTimeSavedLocal(0);
    }
  }, [featureFlags['orgId'], featureFlags['timeSaved'], featureFlags['newTimeSaved'], timeSaved]);

  // use toast to notify user that timeline is being created, but don't block the UI
  const onCreateTimeline = async (creatingEventFromChat?: boolean) => {
    if (caseId && caseId.trim() !== '') {
      // Open modal to prompt for timeline name
      setTimelineName(getCaseDisplayName(caseId)); // Default to case display name (handles '*' → 'All Cases')
      setPendingTimelineCreation({
        creatingEventFromChat
      });
      setIsTimelineNameModalOpen(true);
    }
  };
  const handleCreateTimelineWithName = async () => {
    if (!caseId || caseId.trim() === '') return;
    const nameToUse = timelineName.trim() || caseId;
    const creatingEventFromChat = pendingTimelineCreation?.creatingEventFromChat;
    setIsTimelineNameModalOpen(false);
    setPendingTimelineCreation(null);
    setTimelineName(''); // Reset name

    setToast({
      setOpen: setToastOpen,
      open: true,
      location: 'bottom-right',
      time: Date.now(),
      duration: 3000,
      title: 'Creating Timeline',
      description: 'The system is creating a timeline for this case; you will be notified when it is ready.',
      level: ToastLevel.INFO
    });
    const timeline = await createNewTimeline(nameToUse, creatingEventFromChat);
    await fetchNewTimeline(timeline.id, caseId, false, false);
    setCurrentTimelineId(timeline.id);
    setSelectedSecondOption(timeline.id);
    setIsEditableTimeline(true);
    // Refresh work products for this case
    if (featureFlags?.workProductNav && caseId) {
      fetchWorkProductsForCase(caseId).then(workProducts => {
        setMultipleTimelinesAvailable(workProducts);
      });
    }
  };
  const onCreateThreatReport = async () => {
    setThreatReportKey(undefined);
    if (!caseId || caseId.trim() === '') {
      return;
    }
    const sources: any = {
      fiveCastFiles: [],
      magnetFiles: []
    };
    try {
      const response = await FetchWithVersionCheck(`/api/protected/threatReport/${encodeURIComponent(caseId)}`, {
        method: 'POST',
        body: JSON.stringify({
          action: 'getSources',
          caseId: caseId
        }),
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'x-tai-id-token': authState?.idToken?.idToken || ''
        }
      });
      const sourcesResponse = await response.json();
      const s3Sources = sourcesResponse.sources;
      sources.fiveCastFiles = s3Sources.fiveCastFiles;
      sources.magnetFiles = s3Sources.magnetFiles;
    } catch (error) {
      // do nothing
    }
    if (featureFlags?.threatReportConfig && !!(featureFlags?.threatReportConfig as any).fivecastCaseDirectories[caseId]) {
      sources.fiveCastFiles.push((featureFlags?.threatReportConfig as any).fivecastCaseDirectories[caseId]);
    }
    setThreatReportSources(sources);

    // Pre-populate with case display name
    const caseDisplayName = getCaseDisplayName(caseId);
    setThreatReportName(caseDisplayName);
    setIsThreatReportNameModalOpen(true);
  };
  const handleCreateThreatReportWithName = async () => {
    setThreatReportKey(undefined);
    if (!featureFlags?.workProductNav || !caseId || caseId.trim() === '') return;
    const nameToUse = threatReportName.trim() || getCaseDisplayName(caseId);

    // Generate a unique threat report ID
    const threatReportId = `threat-report-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    setIsThreatReportNameModalOpen(false);
    setThreatReportName('');
    setNewThreatReportId(threatReportId);
    const fivecastServer = (featureFlags?.threatReportConfig as any)?.fivecastServer || null;
    const fivecastDataDirectory = threatReportSources.fiveCastFiles.find((file: any) => file === selectedFivecastSource) || null;
    const magnetDataDirectory = threatReportSources.magnetFiles.find((file: any) => file === selectedMagnetSource) || null;
    if (!magnetDataDirectory && !(fivecastServer && fivecastDataDirectory)) {
      console.log('No fivecast server and fivecast case directory or magnet case directory configured for this case');
      return;
    }
    setIsThreatReportProcessing(true);
    const threatReportIdTokenInfo = getTokenInfo(authState?.idToken?.idToken || '');
    const body: any = {
      query: '',
      deepDive: false,
      threatReport: true,
      selectedCase: caseId,
      caseId: caseId,
      username: getUsername(threatReportIdTokenInfo),
      files: [],
      scopes: [],
      excludedFiles: [],
      excludedScopes: [],
      userId: authState?.idToken?.claims.email || '',
      retryCount: 0,
      refusalMessages: refusalMessages,
      passMessages: passMessages,
      modelArnsList: modelArnsList,
      llmConfig: llmConfig,
      useQueryDecomposition: false,
      useOpenSearch: !!featureFlags?.['useOpenSearchForChat'] || false,
      showPreliminaryResult: true,
      useNewTimeSaved: featureFlags['newTimeSaved']
    };
    if (!!fivecastServer) {
      body.fivecastServer = fivecastServer;
    }
    if (!!fivecastDataDirectory) {
      body.fivecastDataDirectory = fivecastDataDirectory;
    }
    if (!!magnetDataDirectory) {
      body.magnetDataDirectory = magnetDataDirectory;
    }
    if (!!tenantId && tenantId.trim() !== '' && tenantId !== 'null') {
      body.tenant = tenantId;
    }
    try {
      const rawResult = await FetchWithVersionCheck(`/api/protected/chat-service-proxy`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'x-tai-id-token': authState?.idToken?.idToken || ''
        },
        body: JSON.stringify(body)
      });
      if (!rawResult?.ok) {
        setIsThreatReportProcessing(false);
        setToast({
          setOpen: setToastOpen,
          open: true,
          location: 'bottom-right',
          time: Date.now(),
          duration: 5000,
          title: 'Threat report failed to start',
          description: 'Could not start threat report generation. Please try again.',
          level: ToastLevel.ERROR
        });
        return;
      }
      const result = await rawResult.json();
      const sessionIdRaw = result?.sessionId ?? result?.session_id;
      const sessionId = typeof sessionIdRaw === 'string' ? sessionIdRaw.trim() : '';
      const epochTimeSecs = Number(result?.epoch_time_secs);
      const chatKey = `${caseId}-${authState?.idToken?.claims.email || ''}`;
      if (!sessionId || !Number.isFinite(epochTimeSecs) || epochTimeSecs <= 0) {
        setIsThreatReportProcessing(false);
        setToast({
          setOpen: setToastOpen,
          open: true,
          location: 'bottom-right',
          time: Date.now(),
          duration: 5000,
          title: 'Threat report failed to start',
          description: 'The server response was incomplete. Please try again.',
          level: ToastLevel.ERROR
        });
        return;
      }
      const threatReportKey: ThreatReportKey = {
        chatKey,
        sessionId,
        epochTimeSecs,
        requestType: result?.request_type ?? 'threat_report'
      };
      setThreatReportKey(threatReportKey);
      setToast({
        setOpen: setToastOpen,
        open: true,
        location: 'bottom-right',
        time: Date.now(),
        duration: 3000,
        title: 'Creating Threat Report',
        description: 'The system is generating your threat report; you will see progress updates.',
        level: ToastLevel.INFO
      });
      setTimeout(() => {
        setSelectedView('threat-report');
      }, 1000);
    } catch {
      setIsThreatReportProcessing(false);
      setToast({
        setOpen: setToastOpen,
        open: true,
        location: 'bottom-right',
        time: Date.now(),
        duration: 5000,
        title: 'Threat report failed to start',
        description: 'Could not start threat report generation. Please try again.',
        level: ToastLevel.ERROR
      });
    }
  };
  useEffect(() => {
    if (!!featureFlags?.useIntercom) {
      setFeedback(null);
    } else {
      safeHideIntercom();
      setFeedback(Sentry.getFeedback());
    }
  }, [featureFlags?.useIntercom]);
  useEffect(() => {
    if (feedback && feedbackButtonRef.current && !featureFlags?.useIntercom) {
      safeHideIntercom();
      const unsubscribe = feedback.attachTo(feedbackButtonRef.current);
      return unsubscribe;
    }
    return () => {};
  }, [feedback, featureFlags?.useIntercom]);
  const onFeedbackClick = () => {
    if (feedback && feedbackButtonRef.current && !featureFlags?.useIntercom) {
      feedbackButtonRef.current.click();
    } else if (!!featureFlags?.useIntercom) {
      safeShowIntercom();
    }
  };

  // Handle global keyboard shortcut for feedback button
  useEffect(() => {
    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.ctrlKey && e.shiftKey && (e.key === 'f' || e.key === 'F')) {
        if (!featureFlags?.useIntercom) {
          onFeedbackClick();
        } else {
          safeShowIntercom();
        }
      } else if (e.key === 'Escape') {
        setIsFullscreen(false);
      } else if (featureFlags?.sandbox && e.ctrlKey && e.shiftKey && (e.key === 'x' || e.key === 'X')) {
        setSelectedView('sandbox');
      }
    };
    (window as any).addEventListener('keydown', handleKeyDown);
    return () => {
      (window as any).removeEventListener('keydown', handleKeyDown);
    };
  }, [featureFlags?.sandbox, isFullscreen, featureFlags?.useIntercom]);
  const {
    width
  } = useWindowSize();
  useEffect(() => {
    if (width && width < TINY_VIEWPORT_WIDTH) {
      if (!featureFlags?.useIntercom) {
        setShowFeedbackButton(false);
      } else {
        safeHideIntercom();
      }
    } else {
      if (!featureFlags?.useIntercom) {
        setShowFeedbackButton(selectedView !== 'presentation' && selectedView !== 'courtroom');
      } else if (!!featureFlags?.useIntercom) {
        const intercomUserReady = !!authState?.idToken?.claims.email && authState?.idToken?.claims.email !== '' && !!authState?.idToken?.claims.name && authState?.idToken?.claims.name !== '';
        if (intercomJwt && selectedView !== 'presentation' && selectedView !== 'courtroom' && intercomUserReady) {
          const intercomInit = {
            intercom_user_jwt: intercomJwt,
            app_id: 'bab3jdz3',
            user_id: authState?.idToken?.claims.email,
            name: safeIntercomDisplayName(authState?.idToken?.claims.name),
            email: authState?.idToken?.claims.email,
            chat_session_id: chatSessionId ?? '',
            case_id: caseId ?? '',
            selected_view: selectedView ?? ''
          };
          Intercom(intercomInit);
        } else if (!intercomUserReady || selectedView === 'presentation' || selectedView === 'courtroom') {
          safeHideIntercom();
        } else {
          safeHideIntercom();
        }
      }
    }
    if (selectedView !== 'chat') {
      const svgElements = document.querySelectorAll('.svg-highlight-answer-item');
      svgElements.forEach(svg => {
        svg.remove();
      });
    }
  }, [intercomJwt, width, selectedView, featureFlags?.useIntercom, authState?.idToken?.claims.email, authState?.idToken?.claims.name]);
  useEffect(() => {
    setIsFullscreen(false);
  }, [selectedView]);
  const handleOptionChange = (option: string | undefined) => {
    if (option !== caseId) {
      setNewTimelineIsAvailable(false);
      setQuery('');
      setLegacyTimelineIsAvailable(false);
      setSelectedSecondOption(undefined);
      handleSetCaseId(option);
      setCreatingTimeline(false);
      setIsEditableTimeline(false);
      setTimelineCreated(false);

      // Clear work product context when switching cases
      sessionStorage.removeItem('lastWorkProductContext');
      setLastWorkProductView(null);
      setLastWorkProductTimelineId(undefined);
      setLastWorkProductCaseId(undefined);

      // Fetch work products if feature flag is enabled
      if (featureFlags?.workProductNav && option) {
        setMultipleTimelinesAvailable([]);
        setLoadingWorkProducts(true);
        fetchWorkProductsForCase(option).then(workProducts => {
          setMultipleTimelinesAvailable(workProducts);
          setLoadingWorkProducts(false);
        }).catch(() => {
          setLoadingWorkProducts(false);
        });
      } else {
        setMultipleTimelinesAvailable([]);
        setLoadingWorkProducts(false);
      }

      // Always default to chat view when selecting a case
      setSelectedView('chat');
      sessionStorage.setItem('view', 'chat');
      sessionStorage.setItem('case', option || '');
      sessionStorage.setItem('queryKey', '');
    } else if (option === caseId) {
      setDocIdMode(!docIdMode);
    }
  };
  const handleSelectTimelineFromWorkProducts = async (timelineId: string) => {
    // Save context before navigating (default to table view)
    const context = {
      view: 'table' as View,
      timelineId: timelineId,
      caseId: caseId
    };
    setLastWorkProductView('table');
    setLastWorkProductTimelineId(timelineId);
    setLastWorkProductCaseId(caseId);
    sessionStorage.setItem('lastWorkProductContext', JSON.stringify(context));
    setSelectedSecondOption(timelineId);
    setCurrentTimelineId(timelineId);
    await fetchNewTimeline(timelineId, caseId, false);
    // Default to table view when opening from work products
    setSelectedView('table');
    sessionStorage.setItem('view', 'table');
    sessionStorage.setItem('timeline', timelineId);
  };

  // Work products back button must always go directly to the index; do not step through Details/Presentation.
  const handleBackToWorkProducts = () => {
    setSelectedView('work-product-index');
    sessionStorage.setItem('view', 'work-product-index');
  };
  const handleWorkProductViewModeChange = (mode: 'grid' | 'list') => {
    setWorkProductViewMode(mode);
    sessionStorage.setItem('workProductViewMode', mode);
  };
  const handleWorkProductsClick = () => {
    // If already viewing a work product, do nothing
    if (isWorkProductView(selectedView)) {
      return;
    }

    // Check if there's saved work product context
    let contextToRestore = null;
    if (lastWorkProductView && lastWorkProductCaseId) {
      contextToRestore = {
        view: lastWorkProductView,
        timelineId: lastWorkProductTimelineId,
        caseId: lastWorkProductCaseId
      };
    } else {
      const savedContext = sessionStorage.getItem('lastWorkProductContext');
      if (savedContext) {
        try {
          contextToRestore = JSON.parse(savedContext);
        } catch (e) {
          console.error('Failed to parse saved work product context:', e);
        }
      }
    }
    if (contextToRestore) {
      const {
        view
      } = contextToRestore;
      // Immediately update the view state for instant visual feedback
      setSelectedView(view);
      sessionStorage.setItem('view', view);
      // Then restore the full context asynchronously
      handleRestoreWorkProduct();
    } else {
      // No saved context, go to work product index
      setSelectedView('work-product-index');
      sessionStorage.setItem('view', 'work-product-index');
    }
  };
  const handleSelectThreatReport = () => {
    // Save context before navigating
    const context = {
      view: 'threat-report' as View,
      timelineId: undefined,
      caseId: caseId
    };
    setLastWorkProductView('threat-report');
    setLastWorkProductTimelineId(undefined);
    setLastWorkProductCaseId(caseId);
    sessionStorage.setItem('lastWorkProductContext', JSON.stringify(context));
    setIsThreatReportProcessing(false); // just load the last one
    setSelectedView('threat-report');
    sessionStorage.setItem('view', 'threat-report');
  };
  const handleRestoreWorkProduct = async () => {
    // Try to restore from state first, then from sessionStorage
    let contextToRestore = null;
    if (lastWorkProductView && lastWorkProductCaseId) {
      contextToRestore = {
        view: lastWorkProductView,
        timelineId: lastWorkProductTimelineId,
        caseId: lastWorkProductCaseId
      };
    } else {
      const savedContext = sessionStorage.getItem('lastWorkProductContext');
      if (savedContext) {
        try {
          contextToRestore = JSON.parse(savedContext);
        } catch (e) {
          console.error('Failed to parse saved work product context:', e);
        }
      }
    }
    if (contextToRestore) {
      const {
        view,
        timelineId,
        caseId: savedCaseId
      } = contextToRestore;

      // Ensure we have the correct case selected
      if (savedCaseId && savedCaseId !== caseId) {
        handleSetCaseId(savedCaseId);
        // Wait a bit for the case to be set before restoring the timeline
        // The case change will trigger effects that set up the case context
        setTimeout(async () => {
          await restoreWorkProductView(view, timelineId, savedCaseId);
        }, 100);
      } else {
        await restoreWorkProductView(view, timelineId, savedCaseId || caseId);
      }
    } else {
      // No saved context, go to work product index
      setSelectedView('work-product-index');
      sessionStorage.setItem('view', 'work-product-index');
    }
  };
  const restoreWorkProductView = async (view: View, timelineId: string | undefined, caseIdToUse: string | undefined) => {
    // Restore the view
    if (view === 'threat-report') {
      setSelectedView('threat-report');
      sessionStorage.setItem('view', 'threat-report');
    } else if (view === 'table' || view === 'presentation' || view === 'courtroom') {
      // For timeline views, we need to restore the timeline
      if (timelineId && caseIdToUse) {
        const safeView: View = view === 'courtroom' && !featureFlags?.courtroomMode ? 'table' : view;
        // Set the view first so the loading message shows in the correct view
        setSelectedView(safeView);
        sessionStorage.setItem('view', safeView);
        setSelectedSecondOption(timelineId);
        setCurrentTimelineId(timelineId);
        // fetchNewTimeline will set loadingTimeline to true
        await fetchNewTimeline(timelineId, caseIdToUse, false);
        sessionStorage.setItem('timeline', timelineId);
      } else {
        // No timeline ID or case ID, go to work product index
        setSelectedView('work-product-index');
        sessionStorage.setItem('view', 'work-product-index');
      }
    } else {
      // Fallback to work product index
      setSelectedView('work-product-index');
      sessionStorage.setItem('view', 'work-product-index');
    }
  };
  const handleSecondOptionChange = async (optionIn: string | undefined, creatingEventFromChat?: boolean) => {
    if (optionIn) {
      if (optionIn === 'Switch To Doc ID input' || optionIn === 'Create New Timeline') {
        if (optionIn === 'Switch To Doc ID input') {
          sessionStorage.removeItem('timeline');
          sessionStorage.removeItem('view');
          setSelectedSecondOption(undefined);
          setCurrentTimelineId(undefined);
          setQuery('');
          setNewTimelineIsAvailable(false);
          setLegacyTimelineIsAvailable(false);
          setMultipleTimelinesAvailable([]);
          setSelectedSecondOption(undefined);
          setAllSlides(undefined);
          setDocIdMode(true);
        } else {
          // Create New Timeline
          setEntityServicePollAllJobsCount(0);
          setEntityServiceJob({
            jobId: undefined,
            caseId: undefined,
            count: 0
          });
          sessionStorage.removeItem('timeline');
          setSelectedSecondOption(undefined);
          setCurrentTimelineId(undefined);
          setQuery('');
          setNewTimelineIsAvailable(false);
          setLegacyTimelineIsAvailable(false);
          setMultipleTimelinesAvailable([]);
          setSelectedSecondOption(undefined);
          await onCreateTimeline(creatingEventFromChat);
        }
      } else if (optionIn === 'Switch To Timeline Mode') {
        setDocIdMode(false);
        await fetchNewTimeline(undefined, caseId, false);
      } else {
        sessionStorage.setItem('presentation', optionIn);
        setSelectedSecondOption(optionIn || undefined);
        setCurrentTimelineId(optionIn);
        if (creatingEventFromChat && (chatEventToAdd || parsedEventsFromChat)) {
          setPleaseSelectTimeline(false);
          if (chatEventToAdd || parsedEventsFromChat) {
            fetchNewTimeline(optionIn, caseId, false, true);
          }
        } else {
          await fetchNewTimeline(optionIn, caseId, false);
        }
      }
    } else {
      sessionStorage.removeItem('presentation');
    }
  };
  useEffect(() => {
    // check for matching versions on initial reload
    if (featureFlags && sessionStorage.getItem('TimePilotSkewReload') === 'true') {
      // sessionStorage Items: timeline, case, queryKey, view
      const initialCase = sessionStorage.getItem('case') || null;
      const initialTimeline = sessionStorage.getItem('timeline') || null;
      const initialQuery = sessionStorage.getItem('queryKey') || null;
      let initialView = sessionStorage.getItem('view') as View || 'chat';

      // If workProductNav is enabled and we have a case, default to work-product-index
      if (featureFlags?.workProductNav && initialCase && !initialView) {
        initialView = 'work-product-index';
      }
      if (featureFlags?.legacyTimelines && initialQuery) {
        setQuery(initialQuery);
      }
      if (initialCase) {
        handleOptionChange(initialCase);
      }
      if (initialTimeline) {
        handleSecondOptionChange(initialTimeline);
      }
      handleViewChange(initialView as View);
      sessionStorage.removeItem('TimePilotSkewReload');
    }
  }, [featureFlags]);

  // Restore work product context from sessionStorage on mount
  useEffect(() => {
    if (featureFlags?.workProductNav) {
      const savedContext = sessionStorage.getItem('lastWorkProductContext');
      if (savedContext) {
        try {
          const context = JSON.parse(savedContext);
          setLastWorkProductView(context.view);
          setLastWorkProductTimelineId(context.timelineId);
          setLastWorkProductCaseId(context.caseId);
        } catch (e) {
          console.error('Failed to parse saved work product context on mount:', e);
        }
      }

      // Initialize work product view mode from sessionStorage
      const savedViewMode = sessionStorage.getItem('workProductViewMode') as 'grid' | 'list' | null;
      if (savedViewMode === 'grid' || savedViewMode === 'list') {
        setWorkProductViewMode(savedViewMode);
      }
    }
  }, [featureFlags?.workProductNav]);
  const handleViewChange = (view: View) => {
    setIsEventModalOpen(false);
    setIsTitleSlideModalOpen(false);
    if (view === 'courtroom' && !featureFlags?.courtroomMode) {
      view = 'table';
    }
    const previousView = selectedView;
    sessionStorage.setItem('view', view);
    setSelectedView(view);

    // Save work product context when switching from work product view to chat
    if (view === 'chat' && isWorkProductView(previousView)) {
      const context = {
        view: previousView,
        timelineId: currentTimelineId,
        caseId: caseId
      };
      setLastWorkProductView(previousView);
      setLastWorkProductTimelineId(currentTimelineId);
      setLastWorkProductCaseId(caseId);
      sessionStorage.setItem('lastWorkProductContext', JSON.stringify(context));
    }

    // Save context when navigating TO a work product view (update current context)
    if (isWorkProductView(view)) {
      const context = {
        view: view,
        timelineId: currentTimelineId,
        caseId: caseId
      };
      setLastWorkProductView(view);
      setLastWorkProductTimelineId(currentTimelineId);
      setLastWorkProductCaseId(caseId);
      sessionStorage.setItem('lastWorkProductContext', JSON.stringify(context));
    }
    if (view === 'chat') {
      setEntityServicePollAllJobsCount(0);
      setEntityServiceJob({
        jobId: undefined,
        caseId: undefined,
        count: 0
      });
    }
    CheckBuildVersionViaApi();
  };
  const handleQueryChange = (queryKey: string) => {
    setCaseId(undefined);
    setCurrentTimelineId(undefined);
    setNewTimelineIsAvailable(false);
    setIsEditableTimeline(false);
    handleSetCaseId(undefined);
    setQuery(queryKey);
    sessionStorage.setItem('queryKey', queryKey);
    handleViewChange('table');
  };
  useEffect(() => {
    if (query && query.trim() !== '' && legacyTimelineIsAvailable) {
      handleViewChange('table');
    }
  }, [legacyTimelineIsAvailable, query]);
  const updateActivity = async () => {
    lastActivityTimeRef.current = Date.now();
    localStorage.setItem('lastActivityTime', lastActivityTimeRef.current.toString());
  };
  const router = useRouter();
  const handleAuthStateChange = async (nextAuthState: AuthState) => {
    if (!accessToken && !!features && !features?.isCognitoClient) {
      const newAccessToken = await authService.getAccessToken();
      if (newAccessToken) {
        setAccessToken(newAccessToken);
      }
    }
    setAuthState(nextAuthState);
    if (nextAuthState.isAuthenticated) {
      setLastAuthTime(Date.now());
      setAuthStatus(authService.AuthStatus.LoggedIn);
      console.log('User claims:', nextAuthState.idToken?.claims);
      Sentry.setUser({
        username: nextAuthState.idToken?.claims.name ?? '',
        email: nextAuthState.idToken?.claims.email ?? ''
      });
    } else {
      setAuthStatus(authService.AuthStatus.NotLoggedIn);
      Sentry.setUser(null);
    }
  };
  const updateNewAnswer = (answer: Answer) => {
    setNewAnswer(answer);
  };
  useEffect(() => {
    const caseParam = searchParams.get('case');
    if (caseParam && caseParam.trim() !== '') {
      sessionStorage.setItem('caseIdFromUrl', caseParam);
    }

    // OKTA
    const initAuth = async () => {
      try {
        const currentAuthState = await authService.getAuthState();
        const headers = {};
        if (currentAuthState?.idToken?.idToken) {
          (headers as any)['x-tai-id-token'] = currentAuthState?.idToken?.idToken;
        }
        if (currentAuthState?.accessToken?.accessToken) {
          (headers as any)['Authorization'] = `Bearer ${currentAuthState?.accessToken?.accessToken}`;
        }
        // don't use FetchWithVersionCheck here
        const response = await fetch('/api/okta', {
          headers: headers
        });
        const result = await response.json();
        const oktaConfig = {
          issuer: result.okta_issuer,
          clientId: result.okta_client_id,
          redirectUri: result.okta_redirect_url,
          postLogoutRedirectUri: result.post_logout_redirect_url,
          scopes: ['openid', 'profile', 'email'],
          pkce: true
        };
        const newAuthState = await authService.initializeAuth(oktaConfig);
        setIsAuthInitialized(true);
        const initialAccessToken = await authService.getAccessToken();
        setAccessToken(initialAccessToken);
        const authStateToSet = newAuthState || currentAuthState || {};
        setAuthState(authStateToSet);
        authService.subscribeToAuthStateChanges(handleAuthStateChange);
        if (authStateToSet) {
          handleAuthStateChange(authStateToSet);
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
      }
    };
    if (!isAuthInitialized && !!features && !features?.isCognitoClient) {
      initAuth();
    }
  }, [isAuthInitialized, features]);
  useEffect(() => {
    const caseParam = searchParams.get('case');
    if (caseParam && caseParam.trim() !== '') {
      sessionStorage.setItem('caseIdFromUrl', caseParam);
    }
    if (isAuthInitialized && !!features && features?.tokens) {
      // OKTA
      return () => {
        if (isAuthInitialized) {
          authService.unsubscribeFromAuthStateChanges(handleAuthStateChange);
        }
      };
    }
  }, [isAuthInitialized]);
  useEffect(() => {
    const caseParam = searchParams.get('case');
    if (caseParam && caseParam.trim() !== '') {
      sessionStorage.setItem('caseIdFromUrl', caseParam);
    }
    if (isAuthInitialized && authService.isLoginRedirect() && !!features && !(features?.tokens as any)?.decodedIdToken && !(features?.tokens as any)?.decodedAccessToken) {
      // OKTA
      authService.handleLoginRedirect().then(() => {
        authService.getAuthState().then(currentAuthState => {
          if (currentAuthState) {
            handleAuthStateChange(currentAuthState);
          }
        }).catch(error => {
          console.error('Error handling login redirect:', error);
        });
      }).catch(error => {
        console.error('Error handling login redirect:', error);
      });
    }
  }, [isAuthInitialized]);
  const isUserIdle = () => {
    const idleTimeout = authService.IDLE_TIMEOUT;
    const warningTime = IDLE_TIMEOUT_WARNING_MS;
    if (idleTimeout) {
      const lastActivityTime = Math.max(parseInt(localStorage.getItem('lastActivityTime') || '0'), lastActivityTimeRef.current);
      const idleTime = Date.now() - lastActivityTime;
      if (idleTime > idleTimeout - warningTime && idleTime < idleTimeout) {
        const currentTimeUntilLogout = idleTimeout - idleTime - IDLE_BUFFER_MS;
        const logoutInMinutes = Math.floor(currentTimeUntilLogout / 60000);
        const logoutInSeconds = Math.floor(currentTimeUntilLogout % 60000 / 1000);
        const message = logoutInMinutes > 0 ? `You will be logged out in ${logoutInMinutes} minute${logoutInMinutes > 1 ? 's' : ''} and ${logoutInSeconds} second${logoutInSeconds > 1 ? 's' : ''}` : `You will be logged out in ${logoutInSeconds} second${logoutInSeconds > 1 ? 's' : ''}`;
        setToast({
          setOpen: setToastOpen,
          open: true,
          location: 'bottom-right',
          time: Date.now(),
          duration: currentTimeUntilLogout < 9800 ? Math.max(currentTimeUntilLogout, 1) : 9800,
          title: 'Idle Timeout Warning',
          description: message,
          level: ToastLevel.WARNING,
          actionButtonText: 'Click here to stay logged in',
          actionButtonOnClick: () => {
            setToast(undefined);
          }
        });
      }
      if (idleTime > idleTimeout) {
        handleLogout();
      }
      return idleTime > idleTimeout;
    }
    return false;
  };

  // OKTA
  const checkAuthStateCallback = useCallback(async () => {
    if (!isAuthInitialized || !features || features?.isCognitoClient) {
      return;
    }
    await checkAuthState(authStatus, isUserIdle, lastAuthTime, isReLoginAttempted, setAuthState, setIsReLoginAttempted);
    // TODO: decodeToken(authState.accessToken.accessToken).payload.exp - 8hrs; renew/warn before expiration
    // set cookies to use with api calls
    await setAuthCookies(authState as AuthState);
  }, [features, authStatus, lastAuthTime, isReLoginAttempted, authState?.idToken, authState?.accessToken]);

  // OKTA Handle initial auth state when component mounts
  useEffect(() => {
    if (isAuthInitialized && (!features || features?.isCognitoClient)) {
      return;
    }
    checkAuthStateCallback();
  }, [checkAuthStateCallback, features]);
  useEffect(() => {
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll', 'mousemove', 'tp-media-playing'];
    events.forEach(eventName => {
      window.addEventListener(eventName, updateActivity, true);
    });

    // Check every 10 seconds for AuthState or Idle user
    let checkAuthStateInterval = undefined;
    if (isAuthInitialized && features && !features?.isCognitoClient) {
      // OKTA
      checkAuthStateInterval = setInterval(() => {
        if (authStatus !== authService.AuthStatus.LoggedOut) {
          checkAuthStateCallback();
        } else {
          console.log('User has logged out, skipping auth check');
        }
      }, IDLE_BUFFER_MS);
    } else if (features && features?.isCognitoClient) {
      checkAuthStateInterval = setInterval(() => {
        isUserIdle();
      }, IDLE_BUFFER_MS);
    }
    return () => {
      events.forEach(eventName => {
        window.removeEventListener(eventName, updateActivity, true);
      });
      if (checkAuthStateInterval) {
        clearInterval(checkAuthStateInterval);
      }
    };
  }, [authStatus, checkAuthStateCallback, features]);

  // OKTA
  const handleLogin = async () => {
    deleteCookie('PdfPageCounts');
    if (isAuthInitialized && !!features && !(features?.tokens as any)?.decodedIdToken && !(features?.tokens as any)?.decodedAccessToken) {
      // OKTA
      try {
        await authService.handleLogin();
      } catch (error) {
        console.error('Login error:', error);
      }
    } else if (isAuthInitialized && !!features && (features?.tokens as any)?.decodedIdToken && (features?.tokens as any)?.decodedAccessToken) {
      // Cognito
      window.location.reload();
    } else {
      console.error('Auth not initialized');
    }
  };
  const handleLogout = async () => {
    safeShutdownIntercom();
    // Clear session storage first (safe for both Cognito and Okta)
    sessionStorage.removeItem('walkthrus');
    sessionStorage.removeItem('idToken');
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('walkthrus');
    deleteCookie('PdfPageCounts');
    bannerModalAcceptedRef.current = false;
    if (isAuthInitialized) {
      try {
        setAuthState(null);
        setAccessToken(null);
        setIsReLoginAttempted(false);
        lastActivityTimeRef.current = 0;
        localStorage.removeItem('lastActivityTime');
        setLastAuthTime(0);
        if (isCognitoUser) {
          // 🔐 COGNITO LOGOUT FLOW (Option 2)
          setIsCognitoUser(false);
          console.log('Cognito user logout - using Option 2 flow with logout confirmation page');
          // NOTE: Do NOT delete cookies here! The /logout-confirmation page needs them
          // to extract Cognito domain and client ID. That page will handle cookie cleanup.
          window.location.href = '/logout-confirmation';
          return; // Exit function - browser will navigate away
        } else {
          // 🔐 OKTA LOGOUT FLOW (Original)
          console.log('Okta user logout - using original Okta flow');

          // Clear cookies before Okta logout (these don't exist for Okta users but safe to call)
          deleteCookie('accessToken');
          deleteCookie('idToken');
          deleteCookie('x-amzn-oidc-accesstoken');
          deleteCookie('x-amzn-oidc-data');
          deleteCookie('x-amzn-oidc-identity');
          deleteCookie('cognito_id_token');
          deleteCookie('cognito_access_token');
          clearEmployeeTokenForHeimdall();
          deleteCookie('tai_csm_user');
          await authService.handleLogout();
          setAuthStatus(authService.AuthStatus.LoggedIn);
          router.push('/');
        }
      } catch (error) {
        console.error('Logout error:', error);
        sessionStorage.removeItem('isLoggingOut');
        setAuthStatus(authService.AuthStatus.LoggedIn);
      }
    } else {
      console.error('Auth not initialized');
    }
  };

  // set up all walkthrus and allow setting of a specific walkthru
  // to enable walkthrus, set the availableWalkthrus object in the WalkthruContext below like { intro1, intro2 }
  const setWalkthru = async (name: string, walkthru: WalkthruState) => {
    const newWalkthrus: Record<string, WalkthruState> = {
      ...allWalkthrus
    };
    newWalkthrus[name] = walkthru;
    const walkthrusString: string = JSON.stringify(newWalkthrus);
    localStorage.setItem('walkthrus', walkthrusString.toString());
    setAllWalkthrus(newWalkthrus);
  };
  return <StatsContext.Provider value={{
    recordStat,
    userInfo: {
      page: window.location.pathname,
      userId: authState?.idToken?.claims.email ?? '',
      sessionId: chatSessionId ?? '',
      caseId: caseId ?? '',
      query: query
    }
  }} data-sentry-element="unknown" data-sentry-component="Main" data-sentry-source-file="main.tsx">
      <CSMProvider token={authState?.idToken?.idToken || null} data-sentry-element="CSMProvider" data-sentry-source-file="main.tsx">
      <AuthContext.Provider value={{
        idToken: authState?.idToken?.idToken || '',
        accessToken: authState?.accessToken?.accessToken || '',
        tenantName: tenantName || '',
        orgName: orgName || ''
      }} data-sentry-element="unknown" data-sentry-source-file="main.tsx">
      <WalkthruContext.Provider value={{
          walkthrus: allWalkthrus,
          setWalkthru: setWalkthru,
          availableWalkthrus: (featureFlags?.walkthrus || {}) as Record<string, WalkthruItem[]>
        }} data-sentry-element="unknown" data-sentry-source-file="main.tsx">
      <div className="z-[30000] sm:h-screen sm:overflow-x-hidden sm:overflow-y-hidden sm:full-viewport-height">
          <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[99999] focus:px-4 focus:py-2 focus:bg-blue-600 focus:text-white focus:rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
            Skip to main content
          </a>
          <a href="#main-navigation" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[99999] focus:px-4 focus:py-2 focus:bg-blue-600 focus:text-white focus:rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
            Skip to navigation
          </a>
          <ToastProvider swipeDirection="right" duration={toast?.duration || 3000} data-sentry-element="ToastProvider" data-sentry-source-file="main.tsx">
          <ToastContext.Provider value={{
                setToast,
                setToastOpen
              }} data-sentry-element="unknown" data-sentry-source-file="main.tsx">
            <FeaturesContext.Provider value={{
                  flagsFromLD: ldFlags,
                  featureFlags,
                  flagOverrides,
                  setFlagOverrides
                }} data-sentry-element="unknown" data-sentry-source-file="main.tsx">
              <FullScreenContext.Provider value={{
                    isFullscreen,
                    setIsFullscreen,
                    fullscreenMediaItem,
                    setFullscreenMediaItem,
                    linkedMediaItem,
                    setLinkedMediaItem,
                    fullScreenCaptureData: captureData,
                    setFullScreenCaptureData: setCaptureData
                  }} data-sentry-element="unknown" data-sentry-source-file="main.tsx">
                {isAuthInitialized && authState?.isAuthenticated && authState?.accessToken?.accessToken && authState?.accessToken?.accessToken.trim() !== '' && authState?.idToken?.idToken && authState?.idToken?.idToken.trim() !== '' ? featureFlags?.enableMediaAndSourceShare ? <ToolsDrawerProvider caseId={caseId} onAddToTimeline={createEventFromChat}>
                    <ToolsDrawerChrome>
                    <div className="box-border overflow-x-hidden relative sm:h-screen sm:overflow-x-hidden sm:overflow-y-hidden full-viewport-height">
                      {!!toast && <TpToast setOpen={setToastOpen} open={toast.open} title={toast.title} description={toast.description} actionButtonText={toast.actionButtonText} actionButtonOnClick={toast.actionButtonOnClick} level={toast.level} duration={toast.duration} location={toast.location} child={toast.child} time={toast.time} />}
                    {isEventModalOpen && !!timelinePatch && <div className="absolute top-0 left-0 h-screen w-screen z-50 backdrop-blur-sm">
                        <EventModal isOpen={isEventModalOpen} isLegacy={isLegacy || !isEditableTimeline} onClose={handleCloseEventModal} onAddEvent={handleAddEvent} onEditEvent={handleEditEvent} events={allSlides} eventIndex={eventIndex} column={currentColumn} editingEnabled={!isLegacy} sourcesFromChat={allSourcesFromChat?.allSources} mediaSourcesFromChat={allSourcesFromChat?.mediaSources} eventsFromChat={chatEventToAdd ? [chatEventToAdd] : []} caseId={caseId} />
                      </div>}
                    {isTitleSlideModalOpen && !!timelinePatch && <div className="absolute top-0 left-0 h-screen w-screen z-50 backdrop-blur-sm">
                        <TitleSlideModal isOpen={isTitleSlideModalOpen} tags={timelineTags} isLegacy={isLegacy || !isEditableTimeline} onClose={closeTitleSlideModal} onPatchTimeline={handlePatchTimeline} timelinePatch={timelinePatch} editingEnabled={!isLegacy} mediaSourcesFromChat={allSourcesFromChat?.mediaSources} caseId={caseId} />
                      </div>}
                    <NavigationBarHorizontal currentTimelineId={currentTimelineId} selectedOption={caseId || null} onOptionChange={handleOptionChange} selectedView={selectedView} onViewChange={handleViewChange} userId={authState?.idToken?.claims.email ?? ''} onQueryKeyChange={handleQueryChange} newTimelineIsAvailable={newTimelineIsAvailable} legacyTimelineIsAvailable={legacyTimelineIsAvailable} multipleTimelinesAvailable={multipleTimelinesAvailable} loadingTimeline={loadingTimeline} selectedSecondOption={selectedSecondOption || null} onSecondOptionChange={handleSecondOptionChange} clientWidth={width} onFeedbackClick={onFeedbackClick} onCreateTimeline={onCreateTimeline} timelineCreated={timelineCreated} creatingTimeline={creatingTimeline} timelineServiceAppearsOperational={timelineServiceAppearsOperational} onOpenSettings={() => {
                            setSelectedView('settings');
                          }} docIdMode={docIdMode} handleLogout={handleLogout} setTenantName={setTenantName} setRemoteStorageLink={setRemoteStorageLink} setOrgName={setOrgName} isUserAdmin={isUserAdmin} isUserServiceAvailable={isUserServiceAvailable} onWorkProductsClick={handleWorkProductsClick} setBanners={setBanners} className={isEventModalOpen ? 'blur-sm' : ''} />
                    {selectedView === 'work-product-index' && featureFlags?.workProductNav && <WorkProductNavBar featureFlags={featureFlags} caseId={caseId} isIndexView={true} onCreateTimeline={onCreateTimeline} onCreateThreatReport={onCreateThreatReport} viewMode={workProductViewMode} onViewModeChange={handleWorkProductViewModeChange} />}
                    {(selectedView === 'table' || selectedView === 'presentation' || selectedView === 'courtroom') && featureFlags?.workProductNav && <WorkProductNavBar featureFlags={featureFlags} onBackToWorkProducts={handleBackToWorkProducts} onViewChange={handleViewChange} selectedView={selectedView} caseId={caseId} workProductType="timeline" timelineTitle={title} onEditTimelineTitle={handleEditTimelineTitle} isEditableTimeline={isEditableTimeline} />}
                    {selectedView === 'threat-report' && featureFlags?.workProductNav && <WorkProductNavBar featureFlags={featureFlags} onBackToWorkProducts={handleBackToWorkProducts} caseId={caseId} workProductType="threat-report" />}
                    {selectedView === 'chat' && featureFlags?.workProductNav && <WorkProductNavBar featureFlags={featureFlags} onViewChange={handleViewChange} selectedView={selectedView} caseId={caseId} />}
                    {featureFlags?.workProductNav && !isFullscreen && selectedView !== 'presentation' && selectedView !== 'courtroom' && <StatusBar userId={authState?.idToken?.claims.email ?? ''} status={timeSaved ? `Time saved: ${timeSaved} minutes` : 'Time saved: 0 minutes'} timeSaved={timeSaved || 0} loadingTimeSaved={loadingTimeSaved} caseId={caseId || undefined} remoteStorageLink={remoteStorageLinkLocal} />}
                    {isFullscreen && fullscreenMediaItem && <MediaDisplay mediaItem={fullscreenMediaItem} isPresentation={selectedView === 'presentation' || selectedView === 'courtroom' || selectedView === 'chat'} isEventModal={isEventModalOpen} />}
                    {linkedMediaItem && <MediaDisplay mediaItem={linkedMediaItem} isLinkedMedia={true} />}
                    <main id="main-content" role="main" className={`ag-theme-quartz bg-gray-900 fixed w-screen pb-[400px] sm:pb-0 ${selectedView === 'chat' ? `${isFullscreen ? 'opacity-[0.01] overflow-y-hidden' : featureFlags?.workProductNav && caseId ? 'top-[127px] sm:top-[147px] h-[calc(100vh-127px)] sm:h-[calc(100vh-186px)]' : 'top-[56px] sm:top-[67px] h-[calc(100vh-60px)] sm:h-[calc(100vh-74px)]'}` : (selectedView === 'work-product-index' || selectedView === 'table' || selectedView === 'presentation' || selectedView === 'courtroom' || selectedView === 'threat-report') && featureFlags?.workProductNav ? `top-[150px] sm:top-[147px] h-[calc(100vh-150px)] ${selectedView === 'presentation' || selectedView === 'courtroom' ? 'sm:h-[calc(100vh-147px)]' : 'sm:h-[calc(100vh-186px)]'}` : selectedView === 'work-product-index' ? 'top-[55px] sm:top-[67px] h-[calc(100vh-50px)] sm:h-[calc(100vh-74px)]' : 'top-[55px] sm:top-[67px] h-[calc(100vh-50px)] sm:h-[calc(100vh-74px)]'}`}>
                      {selectedView === 'table' && <div className={`ag-theme-quartz w-screen sm:p-[30px] ${isEventModalOpen ? 'opacity-50' : ''}`}>
                          <>
                            {loadingTimeline && <div className='flex flex-col items-center justify-center h-screen gap-2 text-2xl sm:text-4xl font-bold' aria-live="polite" aria-busy="true" role="status">
                                <div>Loading Timeline<span className="animate-fade">.</span><span className="animate-fade delay-500">.</span><span className="animate-fade delay-1000">.</span></div>
                                <SpinnerGapIcon size={42} className="animate-spin-only" aria-hidden="true" />
                              </div>}
                            {(!!currentTimelineId || docIdMode || isLegacy) && !!allSlides && !loadingTimeline && selectedView === 'table' && <TPTable title={title} caseId={caseId || ''} timelineId={currentTimelineId || ''} description={description} media={media} allSlides={allSlides} isLegacy={isLegacy || !isEditableTimeline} updateTimeline={onUpdateTimeline} onEditEvent={handleEditEvent} onOpenModal={openModalFromTable} onSetEventIndex={setEventIndex} onDeleteEvent={handleDeleteEvent} onAddEvent={handleAddEvent} isEditableTimeline={isEditableTimeline} onDeleteTimeline={handleDeleteTimeline} sortByDateTime={sortByDateTime} enrichEvents={createEventsFromParsedEvents} createEventsFromParsedEvents={createEventsFromParsedEvents} entityExtractionJobs={entityExtractionJobsWithQuestions} getEventsFromEntityService={getEventsFromEntityService} createEventsFromEntityEvents={createEventsFromEntityEvents} featureFlags={featureFlags} onViewChange={handleViewChange} onBackToWorkProducts={handleBackToWorkProducts} selectedView={selectedView} onOpenTitleSlideModal={openTitleSlideModal} />}
                            {!currentTimelineId && !docIdMode && !isLegacy && <div className="flex flex-col items-center justify-center h-screen">
                                <div className="text-2xl sm:text-4xl font-bold mb-8">Please select a timeline</div>
                                <Button onPress={() => setIsModalOpen(true)} className="cursor-pointer text-2xl py-6 px-8 font-light animate-glow [box-shadow:0_0_8px_rgba(89,162,247,0.6),0_0_16px_rgba(89,162,247,0.4),0_0_24px_rgba(89,162,247,0.3)]">Timelines Tutorial</Button>
                                <Modal isOpen={isModalOpen} onOpenChange={setIsModalOpen}>
                                  <ModalContent className="max-w-4xl max-h-[90vh]">
                                    <ModalHeader className="flex flex-col gap-1">
                                    </ModalHeader>
                                    <ModalBody className="p-0">
                                      <iframe src="https://tranquility.ai/get-started-guide#timeline" title="Timelines Tutorial" className="w-full h-[70vh] rounded-lg" />
                                    </ModalBody>
                                  </ModalContent>
                                </Modal>
                              </div>}
                          </>
                        </div>}
                      {selectedView === 'presentation' && <div className={`ag-theme-quartz w-screen ${isEventModalOpen ? 'opacity-50' : ''}`}>
                          {loadingTimeline && <div className='flex flex-col items-center justify-center h-screen gap-2 text-2xl sm:text-4xl font-bold'>
                              <div>Loading Timeline<span className="animate-fade">.</span><span className="animate-fade delay-500">.</span><span className="animate-fade delay-1000">.</span></div>
                              <SpinnerGapIcon size={42} className="animate-spin-only" />
                            </div>}
                          {(!!currentTimelineId || docIdMode || isLegacy) && !!allSlides && !loadingTimeline && selectedView === 'presentation' && <TPPresentation title={title} description={description} media={media} allSlides={allSlides?.filter(slide => slide.show) || []} onOpenModal={openModalFromPresentation} onOpenTitleSlideModal={openTitleSlideModal} isEventModalOpen={isEventModalOpen} docIdMode={docIdMode} featureFlags={featureFlags} />}
                          {!currentTimelineId && !docIdMode && !isLegacy && <div className="flex flex-col items-center justify-center h-screen">
                              <div className="text-2xl sm:text-4xl font-bold mb-8">Please select a timeline</div>
                              <Button onPress={() => setIsModalOpen(true)} className="cursor-pointer text-lg sm:text-2xl py-6 px-8 font-light animate-glow [box-shadow:0_0_8px_rgba(89,162,247,0.6),0_0_16px_rgba(89,162,247,0.4),0_0_24px_rgba(89,162,247,0.3)]">Timelines Tutorial</Button>
                              <Modal isOpen={isModalOpen} onOpenChange={setIsModalOpen}>
                                <ModalContent className="max-w-4xl max-h-[90vh]">
                                  <ModalHeader className="flex flex-col gap-1">
                                  </ModalHeader>
                                  <ModalBody className="p-0">
                                    <iframe src="https://tranquility.ai/get-started-guide#timeline" title="Timelines Tutorial" className="w-full h-[70vh] rounded-lg" />
                                  </ModalBody>
                                </ModalContent>
                              </Modal>
                            </div>}
                        </div>}
                      {selectedView === 'courtroom' && featureFlags?.courtroomMode && <div className={`ag-theme-quartz h-full w-screen ${isEventModalOpen ? 'opacity-50' : ''}`}>
                          {loadingTimeline && <div className='flex flex-col items-center justify-center h-screen gap-2 text-2xl sm:text-4xl font-bold'>
                              <div>Loading Timeline<span className="animate-fade">.</span><span className="animate-fade delay-500">.</span><span className="animate-fade delay-1000">.</span></div>
                              <SpinnerGapIcon size={42} className="animate-spin-only" />
                            </div>}
                          {(!!currentTimelineId || docIdMode || isLegacy) && !!allSlides && !loadingTimeline && selectedView === 'courtroom' && <TPCourtroom title={title} description={description} media={media} allSlides={allSlides?.filter(slide => slide.show) || []} />}
                          {!currentTimelineId && !docIdMode && !isLegacy && <div className="flex flex-col items-center justify-center h-screen">
                              <div className="text-2xl sm:text-4xl font-bold mb-8">Please select a timeline</div>
                            </div>}
                        </div>}
                      {selectedView === 'work-product-index' && <div className="w-full h-full overflow-y-auto overflow-x-hidden">
                          <WorkProductIndex caseId={caseId} timelines={multipleTimelinesAvailable} onCreateTimeline={onCreateTimeline} onSelectTimeline={handleSelectTimelineFromWorkProducts} onSelectThreatReport={handleSelectThreatReport} featureFlags={featureFlags} isLoading={loadingWorkProducts} viewMode={workProductViewMode} threatReportJson={threatReportJson} isThreatReportProcessing={isThreatReportProcessing} processingThreatReportId={newThreatReportId} clearThreatReportKey={() => setThreatReportKey(undefined)} />
                        </div>}
                      {selectedView === 'threat-report' && <div className="threat-report w-full h-full">
                          <ThreatReport threatReportKey={threatReportKey} userId={authState?.idToken?.claims.email ?? ''} tenant={tenantId ?? null} caseId={caseId} reportNumber={threatReportJson?.reportNumber} profile={threatReportJson?.profile} contactInfo={threatReportJson?.contactInfo} keyAssociates={threatReportJson?.keyAssociates} actionableThreats={threatReportJson?.actionableThreats} socialMediaPosts={threatReportJson?.socialMediaPosts} threatAssessment={threatReportJson?.threatAssessment} narrativeThreatAssessment={threatReportJson?.narrativeThreatAssessment} setIsThreatReportProcessing={setIsThreatReportProcessing} clearThreatReportKey={() => setThreatReportKey(undefined)} />
                        </div>}
                      <div className={`${selectedView === 'chat' && !isFullscreen ? '' : 'h-[1px] w-[1px] max-h-[1px] max-w-[1px] opacity-0 z-0 overflow-hidden'} ag-theme-quartz ${isEventModalOpen ? 'opacity-50' : ''}`}>
                        {!!pleaseSelectTimeline && <div className="box-border absolute top-[-5px] left-0 w-full h-[calc(100vh-110px)] bg-gray-900 opacity-95 z-[30000]">
                            <Card className="box-border backdrop-blur-md top-1/2 left-1/2 h-[500px] w-[300px] translate-x-[-50%] translate-y-[-50%] bg-gray-800 opacity-100">
                              <CardHeader className="box-border top-0 left-0 bg-white w-full h-[50px] flex flex-row items-center justify-center">
                                <div className="text-lg text-black">
                                  {currentTimelineId ? 'Confirm Timeline' : 'Please select a timeline'}
                                </div>
                              </CardHeader>
                              <CardBody className="box-border flex flex-col items-center justify-top">
                                <div className="flex flex-row items-center justify-center p-2 text-center">
                                  <div>
                                    {currentTimelineId ? 'The event will be added to the selected timeline. You can change the timeline below if needed.' : 'This action requires a timeline. Please select one or cancel this action.'}
                                  </div>
                                </div>
                                <div className="box-border flex flex-row items-center justify-center mb-1 pb-1 w-full">
                                  <Select aria-label="Select timeline" styles={SelectStyles} className="react-select-container font-inter z-[9999] w-full custom-scrollbar2" value={modalSelectedTimeline ? {
                                        value: modalSelectedTimeline,
                                        label: (() => {
                                          const timeline = multipleTimelinesAvailable.find(t => t.id === modalSelectedTimeline);
                                          if (timeline) {
                                            const createdBy = timeline.createdBy ? timeline.createdBy.split('@')[0] : '';
                                            const createdAt = timeline.createdAt ? new Date(timeline.createdAt).toLocaleDateString('en-US', {
                                              year: 'numeric',
                                              month: '2-digit',
                                              day: '2-digit'
                                            }) : '';
                                            const title = timeline.title ? timeline.title : '';
                                            return `${createdBy} | ${timeline.id} | ${createdAt} | ${title}`;
                                          }
                                          return modalSelectedTimeline;
                                        })()
                                      } : null} onChange={newValue => {
                                        const value = (newValue as SingleValue<{
                                          value: string;
                                          label: string;
                                        }>)?.value;
                                        if (value === 'Create New Timeline') {
                                          setPleaseSelectTimeline(false);
                                          handleSecondOptionChange(value, true);
                                        } else {
                                          setModalSelectedTimeline(value);
                                        }
                                      }} options={multipleTimelinesAvailable && multipleTimelinesAvailable.length > 0 ? [{
                                        value: 'Create New Timeline',
                                        label: 'Create New Timeline'
                                      }, ...multipleTimelinesAvailable.map(item => {
                                        const createdBy = item.createdBy ? item.createdBy.split('@')[0] : '';
                                        const createdAt = item.createdAt ? new Date(item.createdAt).toLocaleDateString('en-US', {
                                          year: 'numeric',
                                          month: '2-digit',
                                          day: '2-digit'
                                        }) : '';
                                        const title = item.title ? item.title : '';
                                        return {
                                          value: item.id,
                                          label: `${createdBy} | ${item.id} | ${createdAt} | ${title}`
                                        };
                                      })] : [{
                                        value: 'Create New Timeline',
                                        label: 'Create New Timeline'
                                      }, {
                                        value: '',
                                        label: 'No timelines available',
                                        isDisabled: true
                                      }]} placeholder={'Select a timeline'} noOptionsMessage={() => "No cases available with that name"} />
                                </div>
                              </CardBody>
                              <CardFooter className="box-border bg-white flex flex-row items-center justify-center gap-2">
                                <Button onPress={() => handleCancelPleaseSelectTimeline()}>Cancel</Button>
                                <Button color="primary" isDisabled={!modalSelectedTimeline} onPress={() => {
                                      if (modalSelectedTimeline) {
                                        setPleaseSelectTimeline(false);
                                        // Update the current timeline and proceed with event creation
                                        setSelectedSecondOption(modalSelectedTimeline);
                                        setCurrentTimelineId(modalSelectedTimeline);
                                        // fetchNewTimeline will call proceedWithChatEvent after loading
                                        fetchNewTimeline(modalSelectedTimeline, caseId, false, true);
                                      }
                                    }}>
                                  Add Event
                                </Button>
                              </CardFooter>
                            </Card>
                          </div>}
                        <div className={`overflow-y-hidden ${selectedView !== 'chat' || isFullscreen ? 'opacity-[0.01] top-0 left-0 h-[1px] w-[1px] max-h-[1px] max-w-[1px] overflow-hidden' : ''}`}>
                          <Chat featureFlags={featureFlags} selectedOption={caseId ?? ''} userName={authState?.idToken?.claims.name ?? ''} userId={authState?.idToken?.claims.email ?? ''} addToTimeline={createEventFromChat} addMultipleToTimeline={createEventsFromParsedEvents} isEditableTimeline={isEditableTimeline} updateNewAnswer={updateNewAnswer} setSourcesFromChat={setSourcesFromChat} swapSessionId={swapSessionId} setMainChatSessionId={setMainChatSessionId} entityExtractionJobs={entityExtractionJobs} chatHistorySessions={chatHistorySessions} createEventsFromEntityEvents={createEventsFromEntityEvents} getEventsFromEntityService={getEventsFromEntityService} />
                        </div>
                      </div>
                      {selectedView === 'settings' && <div className={`ag-theme-quartz w-screen p-[20px] ${isEventModalOpen ? 'opacity-50' : ''}`}>
                          <AdminPanel onBackToDashboard={() => handleViewChange('chat')} />
                        </div>}
                      {featureFlags?.sandbox && selectedView === 'sandbox' && <div className={`ag-theme-quartz w-screen p-[20px] ${isEventModalOpen ? 'opacity-50' : ''}`}>
                          <Sandbox onClose={() => {
                                setSelectedView('chat');
                              }} />
                        </div>}
                    </main>
                    <ToastViewport className="p-0 m-0 h-screen w-screen overflow-hidden z-[10001]" />
                  </div>
                    </ToolsDrawerChrome>
                  </ToolsDrawerProvider> : <div className="box-border overflow-x-hidden relative sm:h-screen sm:overflow-x-hidden sm:overflow-y-hidden full-viewport-height">
                    {!!toast && <TpToast setOpen={setToastOpen} open={toast.open} title={toast.title} description={toast.description} actionButtonText={toast.actionButtonText} actionButtonOnClick={toast.actionButtonOnClick} level={toast.level} duration={toast.duration} location={toast.location} child={toast.child} time={toast.time} />}
                    {isEventModalOpen && !!timelinePatch && <div className="absolute top-0 left-0 h-screen w-screen z-50 backdrop-blur-sm">
                        <EventModal isOpen={isEventModalOpen} isLegacy={isLegacy || !isEditableTimeline} onClose={handleCloseEventModal} onAddEvent={handleAddEvent} onEditEvent={handleEditEvent} events={allSlides} eventIndex={eventIndex} column={currentColumn} editingEnabled={!isLegacy} sourcesFromChat={allSourcesFromChat?.allSources} mediaSourcesFromChat={allSourcesFromChat?.mediaSources} eventsFromChat={chatEventToAdd ? [chatEventToAdd] : []} caseId={caseId} />
                      </div>}
                    {isTitleSlideModalOpen && !!timelinePatch && <div className="absolute top-0 left-0 h-screen w-screen z-50 backdrop-blur-sm">
                        <TitleSlideModal isOpen={isTitleSlideModalOpen} tags={timelineTags} isLegacy={isLegacy || !isEditableTimeline} onClose={closeTitleSlideModal} onPatchTimeline={handlePatchTimeline} timelinePatch={timelinePatch} editingEnabled={!isLegacy} mediaSourcesFromChat={allSourcesFromChat?.mediaSources} caseId={caseId} />
                      </div>}
                    <NavigationBarHorizontal currentTimelineId={currentTimelineId} selectedOption={caseId || null} onOptionChange={handleOptionChange} selectedView={selectedView} onViewChange={handleViewChange} userId={authState?.idToken?.claims.email ?? ''} onQueryKeyChange={handleQueryChange} newTimelineIsAvailable={newTimelineIsAvailable} legacyTimelineIsAvailable={legacyTimelineIsAvailable} multipleTimelinesAvailable={multipleTimelinesAvailable} loadingTimeline={loadingTimeline} selectedSecondOption={selectedSecondOption || null} onSecondOptionChange={handleSecondOptionChange} clientWidth={width} onFeedbackClick={onFeedbackClick} onCreateTimeline={onCreateTimeline} timelineCreated={timelineCreated} creatingTimeline={creatingTimeline} timelineServiceAppearsOperational={timelineServiceAppearsOperational} onOpenSettings={() => {
                        setSelectedView('settings');
                      }} docIdMode={docIdMode} handleLogout={handleLogout} setTenantName={setTenantName} setRemoteStorageLink={setRemoteStorageLink} setOrgName={setOrgName} isUserAdmin={isUserAdmin} isUserServiceAvailable={isUserServiceAvailable} onWorkProductsClick={handleWorkProductsClick} setBanners={setBanners} className={isEventModalOpen ? 'blur-sm' : ''} />
                    {selectedView === 'work-product-index' && featureFlags?.workProductNav && <WorkProductNavBar featureFlags={featureFlags} caseId={caseId} isIndexView={true} onCreateTimeline={onCreateTimeline} onCreateThreatReport={onCreateThreatReport} viewMode={workProductViewMode} onViewModeChange={handleWorkProductViewModeChange} />}
                    {(selectedView === 'table' || selectedView === 'presentation') && featureFlags?.workProductNav && <WorkProductNavBar featureFlags={featureFlags} onBackToWorkProducts={handleBackToWorkProducts} onViewChange={handleViewChange} selectedView={selectedView} caseId={caseId} workProductType="timeline" />}
                    {selectedView === 'threat-report' && featureFlags?.workProductNav && <WorkProductNavBar featureFlags={featureFlags} onBackToWorkProducts={handleBackToWorkProducts} caseId={caseId} workProductType="threat-report" />}
                    {selectedView === 'chat' && !isFullscreen && <StatusBar userId={authState?.idToken?.claims.email ?? ''} status={timeSaved ? `Time saved: ${timeSaved} minutes` : 'Time saved: 0 minutes'} timeSaved={timeSaved || 0} loadingTimeSaved={loadingTimeSaved} caseId={caseId || undefined} remoteStorageLink={remoteStorageLinkLocal} />}
                    {isFullscreen && fullscreenMediaItem && <MediaDisplay mediaItem={fullscreenMediaItem} isPresentation={selectedView === 'presentation' || selectedView === 'chat'} isEventModal={isEventModalOpen} />}
                    {linkedMediaItem && <MediaDisplay mediaItem={linkedMediaItem} isLinkedMedia={true} />}
                    <main id="main-content" role="main" className={`ag-theme-quartz bg-gray-900 fixed w-screen pb-[400px] sm:pb-0 ${selectedView === 'chat' ? `${isFullscreen ? 'opacity-[0.01] overflow-y-hidden' : 'sm:top-[111px] top-[95px] h-[calc(100vh-99px)] sm:h-[calc(100vh-114px)]'}` : (selectedView === 'work-product-index' || selectedView === 'table' || selectedView === 'presentation' || selectedView === 'threat-report') && featureFlags?.workProductNav ? 'top-[150px] sm:top-[147px] h-[calc(100vh-150px)] sm:h-[calc(100vh-147px)]' : selectedView === 'work-product-index' ? 'top-[55px] sm:top-[67px] h-[calc(100vh-50px)] sm:h-[calc(100vh-74px)]' : 'top-[55px] sm:top-[67px] h-[calc(100vh-50px)] sm:h-[calc(100vh-74px)]'}`}>
                      {selectedView === 'table' && <div className={`ag-theme-quartz w-screen sm:p-[30px] ${isEventModalOpen ? 'opacity-50' : ''}`}>
                          <>
                            {loadingTimeline && <div className='flex flex-col items-center justify-center h-screen gap-2 text-2xl sm:text-4xl font-bold' aria-live="polite" aria-busy="true" role="status">
                                <div>Loading Timeline<span className="animate-fade">.</span><span className="animate-fade delay-500">.</span><span className="animate-fade delay-1000">.</span></div>
                                <SpinnerGapIcon size={42} className="animate-spin-only" aria-hidden="true" />
                              </div>}
                            {(!!currentTimelineId || docIdMode || isLegacy) && !!allSlides && !loadingTimeline && selectedView === 'table' && <TPTable title={title} caseId={caseId || ''} timelineId={currentTimelineId || ''} description={description} media={media} allSlides={allSlides} isLegacy={isLegacy || !isEditableTimeline} updateTimeline={onUpdateTimeline} onEditEvent={handleEditEvent} onOpenModal={openModalFromTable} onSetEventIndex={setEventIndex} onDeleteEvent={handleDeleteEvent} onAddEvent={handleAddEvent} isEditableTimeline={isEditableTimeline} onDeleteTimeline={handleDeleteTimeline} sortByDateTime={sortByDateTime} enrichEvents={createEventsFromParsedEvents} createEventsFromParsedEvents={createEventsFromParsedEvents} entityExtractionJobs={entityExtractionJobsWithQuestions} getEventsFromEntityService={getEventsFromEntityService} createEventsFromEntityEvents={createEventsFromEntityEvents} featureFlags={featureFlags} onViewChange={handleViewChange} onBackToWorkProducts={handleBackToWorkProducts} selectedView={selectedView} onOpenTitleSlideModal={openTitleSlideModal} />}
                            {!currentTimelineId && !docIdMode && !isLegacy && <div className="flex flex-col items-center justify-center h-screen">
                                <div className="text-2xl sm:text-4xl font-bold mb-8">Please select a timeline</div>
                                <Button onPress={() => setIsModalOpen(true)} className="cursor-pointer text-2xl py-6 px-8 font-light animate-glow [box-shadow:0_0_8px_rgba(89,162,247,0.6),0_0_16px_rgba(89,162,247,0.4),0_0_24px_rgba(89,162,247,0.3)]">Timelines Tutorial</Button>
                                <Modal isOpen={isModalOpen} onOpenChange={setIsModalOpen}>
                                  <ModalContent className="max-w-4xl max-h-[90vh]">
                                    <ModalHeader className="flex flex-col gap-1">
                                    </ModalHeader>
                                    <ModalBody className="p-0">
                                      <iframe src="https://tranquility.ai/get-started-guide#timeline" title="Timelines Tutorial" className="w-full h-[70vh] rounded-lg" />
                                    </ModalBody>
                                  </ModalContent>
                                </Modal>
                              </div>}
                          </>
                        </div>}
                      {selectedView === 'presentation' && <div className={`ag-theme-quartz w-screen ${isEventModalOpen ? 'opacity-50' : ''}`}>
                          {loadingTimeline && <div className='flex flex-col items-center justify-center h-screen gap-2 text-2xl sm:text-4xl font-bold'>
                              <div>Loading Timeline<span className="animate-fade">.</span><span className="animate-fade delay-500">.</span><span className="animate-fade delay-1000">.</span></div>
                              <SpinnerGapIcon size={42} className="animate-spin-only" />
                            </div>}
                          {(!!currentTimelineId || docIdMode || isLegacy) && !!allSlides && !loadingTimeline && selectedView === 'presentation' && <TPPresentation title={title} description={description} media={media} allSlides={allSlides?.filter(slide => slide.show) || []} onOpenModal={openModalFromPresentation} onOpenTitleSlideModal={openTitleSlideModal} isEventModalOpen={isEventModalOpen} docIdMode={docIdMode} featureFlags={featureFlags} />}
                          {!currentTimelineId && !docIdMode && !isLegacy && <div className="flex flex-col items-center justify-center h-screen">
                              <div className="text-2xl sm:text-4xl font-bold mb-8">Please select a timeline</div>
                              <Button onPress={() => setIsModalOpen(true)} className="cursor-pointer text-lg sm:text-2xl py-6 px-8 font-light animate-glow [box-shadow:0_0_8px_rgba(89,162,247,0.6),0_0_16px_rgba(89,162,247,0.4),0_0_24px_rgba(89,162,247,0.3)]">Timelines Tutorial</Button>
                                <Modal isOpen={isModalOpen} onOpenChange={setIsModalOpen}>
                                  <ModalContent className="max-w-4xl max-h-[90vh]">
                                    <ModalHeader className="flex flex-col gap-1">
                                    </ModalHeader>
                                    <ModalBody className="p-0">
                                      <iframe src="https://tranquility.ai/get-started-guide#timeline" title="Timelines Tutorial" className="w-full h-[70vh] rounded-lg" />
                                    </ModalBody>
                                  </ModalContent>
                                </Modal>
                            </div>}
                        </div>}
                      {selectedView === 'work-product-index' && <div className="w-full h-full overflow-y-auto overflow-x-hidden">
                          <WorkProductIndex caseId={caseId} timelines={multipleTimelinesAvailable} onCreateTimeline={onCreateTimeline} onSelectTimeline={handleSelectTimelineFromWorkProducts} onSelectThreatReport={handleSelectThreatReport} featureFlags={featureFlags} isLoading={loadingWorkProducts} viewMode={workProductViewMode} threatReportJson={threatReportJson} isThreatReportProcessing={isThreatReportProcessing} processingThreatReportId={newThreatReportId} clearThreatReportKey={() => setThreatReportKey(undefined)} />
                        </div>}
                      {selectedView === 'threat-report' && <div className="threat-report w-full h-full">
                          <ThreatReport threatReportKey={threatReportKey} userId={authState?.idToken?.claims.email ?? ''} tenant={tenantId ?? null} caseId={caseId} reportNumber={threatReportJson?.reportNumber} profile={threatReportJson?.profile} contactInfo={threatReportJson?.contactInfo} keyAssociates={threatReportJson?.keyAssociates} actionableThreats={threatReportJson?.actionableThreats} socialMediaPosts={threatReportJson?.socialMediaPosts} threatAssessment={threatReportJson?.threatAssessment} narrativeThreatAssessment={threatReportJson?.narrativeThreatAssessment} setIsThreatReportProcessing={setIsThreatReportProcessing} clearThreatReportKey={() => setThreatReportKey(undefined)} />
                        </div>}
                      <div className={`${selectedView === 'chat' && !isFullscreen ? '' : 'h-[1px] w-[1px] max-h-[1px] max-w-[1px] opacity-0 z-0 overflow-hidden'} ag-theme-quartz ${isEventModalOpen ? 'opacity-50' : ''}`}>
                        {!!pleaseSelectTimeline && <div className="box-border absolute top-[-5px] left-0 w-full h-[calc(100vh-110px)] bg-gray-900 opacity-95 z-[30000]">
                            <Card className="box-border backdrop-blur-md top-1/2 left-1/2 h-[500px] w-[300px] translate-x-[-50%] translate-y-[-50%] bg-gray-800 opacity-100">
                              <CardHeader className="box-border top-0 left-0 bg-white w-full h-[50px] flex flex-row items-center justify-center">
                                <div className="text-lg text-black">
                                  {currentTimelineId ? 'Confirm Timeline' : 'Please select a timeline'}
                                </div>
                              </CardHeader>
                              <CardBody className="box-border flex flex-col items-center justify-top">
                                <div className="flex flex-row items-center justify-center p-2 text-center">
                                  <div>
                                    {currentTimelineId ? 'The event will be added to the selected timeline. You can change the timeline below if needed.' : 'This action requires a timeline. Please select one or cancel this action.'}
                                  </div>
                                </div>
                                <div className="box-border flex flex-row items-center justify-center mb-1 pb-1 w-full">
                                  <Select aria-label="Select timeline" styles={SelectStyles} className="react-select-container font-inter z-[9999] w-full custom-scrollbar2" value={modalSelectedTimeline ? {
                                    value: modalSelectedTimeline,
                                    label: (() => {
                                      const timeline = multipleTimelinesAvailable.find(t => t.id === modalSelectedTimeline);
                                      if (timeline) {
                                        const createdBy = timeline.createdBy ? timeline.createdBy.split('@')[0] : '';
                                        const createdAt = timeline.createdAt ? new Date(timeline.createdAt).toLocaleDateString('en-US', {
                                          year: 'numeric',
                                          month: '2-digit',
                                          day: '2-digit'
                                        }) : '';
                                        const title = timeline.title ? timeline.title : '';
                                        return `${createdBy} | ${timeline.id} | ${createdAt} | ${title}`;
                                      }
                                      return modalSelectedTimeline;
                                    })()
                                  } : null} onChange={newValue => {
                                    const value = (newValue as SingleValue<{
                                      value: string;
                                      label: string;
                                    }>)?.value;
                                    if (value === 'Create New Timeline') {
                                      setPleaseSelectTimeline(false);
                                      handleSecondOptionChange(value, true);
                                    } else {
                                      setModalSelectedTimeline(value);
                                    }
                                  }} options={multipleTimelinesAvailable && multipleTimelinesAvailable.length > 0 ? [{
                                    value: 'Create New Timeline',
                                    label: 'Create New Timeline'
                                  }, ...multipleTimelinesAvailable.map(item => {
                                    const createdBy = item.createdBy ? item.createdBy.split('@')[0] : '';
                                    const createdAt = item.createdAt ? new Date(item.createdAt).toLocaleDateString('en-US', {
                                      year: 'numeric',
                                      month: '2-digit',
                                      day: '2-digit'
                                    }) : '';
                                    const title = item.title ? item.title : '';
                                    return {
                                      value: item.id,
                                      label: `${createdBy} | ${item.id} | ${createdAt} | ${title}`
                                    };
                                  })] : [{
                                    value: 'Create New Timeline',
                                    label: 'Create New Timeline'
                                  }, {
                                    value: '',
                                    label: 'No timelines available',
                                    isDisabled: true
                                  }]} placeholder={'Select a timeline'} noOptionsMessage={() => "No cases available with that name"} />
                                </div>
                              </CardBody>
                              <CardFooter className="box-border bg-white flex flex-row items-center justify-center gap-2">
                                <Button onPress={() => handleCancelPleaseSelectTimeline()}>Cancel</Button>
                                <Button color="primary" isDisabled={!modalSelectedTimeline} onPress={() => {
                                  if (modalSelectedTimeline) {
                                    setPleaseSelectTimeline(false);
                                    setSelectedSecondOption(modalSelectedTimeline);
                                    setCurrentTimelineId(modalSelectedTimeline);
                                    fetchNewTimeline(modalSelectedTimeline, caseId, false, true);
                                  }
                                }}>
                                  Add Event
                                </Button>
                              </CardFooter>
                            </Card>
                          </div>}
                        <div className={`overflow-y-hidden ${selectedView !== 'chat' || isFullscreen ? 'opacity-[0.01] top-0 left-0 h-[1px] w-[1px] max-h-[1px] max-w-[1px] overflow-hidden' : ''}`}>
                          <Chat selectedOption={caseId ?? ''} userName={authState?.idToken?.claims.name ?? ''} userId={authState?.idToken?.claims.email ?? ''} addToTimeline={createEventFromChat} addMultipleToTimeline={createEventsFromParsedEvents} isEditableTimeline={isEditableTimeline} featureFlags={featureFlags} updateNewAnswer={updateNewAnswer} setSourcesFromChat={setSourcesFromChat} swapSessionId={swapSessionId} setMainChatSessionId={setMainChatSessionId} entityExtractionJobs={entityExtractionJobs} chatHistorySessions={chatHistorySessions} createEventsFromEntityEvents={createEventsFromEntityEvents} getEventsFromEntityService={getEventsFromEntityService} />
                        </div>
                      </div>
                      {selectedView === 'settings' && <div className={`ag-theme-quartz w-screen p-[20px] ${isEventModalOpen ? 'opacity-50' : ''}`}>
                          <AdminPanel onBackToDashboard={() => handleViewChange('chat')} />
                        </div>}
                      {featureFlags?.sandbox && selectedView === 'sandbox' && <div className={`ag-theme-quartz w-screen p-[20px] ${isEventModalOpen ? 'opacity-50' : ''}`}>
                          <Sandbox onClose={() => {
                            setSelectedView('chat');
                          }} />
                        </div>}
                    </main>
                    <ToastViewport className="p-0 m-0 h-screen w-screen overflow-hidden z-[10001]" />
                  </div> : <main className="flex flex-col items-center justify-center h-screen w-screen">
                    <div id="logo" className="flex flex-row items-center justify-center gap-2 sm:gap-4 leading-normal tracking-normal mq480:flex-wrap mb-10 sm:mb-20">
                      <Image className="relative" loading="lazy" alt="TimePilot logo" src="/tp-logotype.png" height={logoHeight} width={logoWidth} />
                    </div>
                    {!!timeSavedLocal && timeSavedLocal > 0 && <div className="flex flex-row gap-2 items-center justify-center text-sm opacity-70 mb-14">
                        <div className="flex flex-row items-center justify-center gap-1 text-lg p-4 sm:p-0 sm:text-3xl font-extralight font-inter">
                          Your organization has saved {formatTime(timeSavedLocal)}
                        </div>
                      </div>}
                    {isAuthInitialized && <button onClick={handleLogin} className="cursor-pointer w-20 h-8 sm:w-32 sm:h-10 text-white text-sm sm:text-lg font-inter font-normal bg-brand-blue rounded-md text-center">
                        Log In
                      </button>}
                    <div className="mt-8 px-6 text-center text-xs sm:text-sm text-white/70 max-w-[700px]">
                      Access and use of TimePilot is governed by Tranquility AI&apos;s{" "}
                      <a href="https://tranquility.ai/terms/enterprise" target="_blank" rel="noopener noreferrer" className="text-white underline hover:text-white/90">
                        Terms
                      </a>
                    </div>
                  </main>}
                <Modal isOpen={isTimelineNameModalOpen} onOpenChange={setIsTimelineNameModalOpen} isDismissable onClose={() => {
                      setIsTimelineNameModalOpen(false);
                      setPendingTimelineCreation(null);
                      setTimelineName('');
                    }} data-sentry-element="Modal" data-sentry-source-file="main.tsx">
                  <ModalContent className='bg-gray-900 border-2 border-gray-200 rounded-lg text-white' data-sentry-element="ModalContent" data-sentry-source-file="main.tsx">
                    <ModalHeader className='bg-gray-800 px-4' data-sentry-element="ModalHeader" data-sentry-source-file="main.tsx">
                      Create New Timeline
                    </ModalHeader>
                    <ModalBody className='bg-gray-700 px-4 py-4' data-sentry-element="ModalBody" data-sentry-source-file="main.tsx">
                      <Input label="Timeline Name" labelPlacement="outside" placeholder="Enter timeline name" value={timelineName} onChange={e => setTimelineName(e.target.value)} onKeyDown={e => {
                            if (e.key === 'Enter' && timelineName.trim()) {
                              handleCreateTimelineWithName();
                            }
                          }} autoFocus classNames={{
                            base: 'w-full',
                            inputWrapper: 'w-90'
                          }} data-sentry-element="Input" data-sentry-source-file="main.tsx" />
                    </ModalBody>
                    <ModalFooter className='bg-gray-800 px-4' data-sentry-element="ModalFooter" data-sentry-source-file="main.tsx">
                      <Button className='bg-gray-300 hover:bg-gray-300 border-gray-600 h-[30px]' onPress={() => {
                            setIsTimelineNameModalOpen(false);
                            setPendingTimelineCreation(null);
                            setTimelineName('');
                          }} data-sentry-element="Button" data-sentry-source-file="main.tsx">
                        Cancel
                      </Button>
                      <Button className='bg-blue-600 hover:bg-blue-700 text-white h-[30px]' onPress={handleCreateTimelineWithName} isDisabled={!timelineName.trim()} data-sentry-element="Button" data-sentry-source-file="main.tsx">
                        Create
                      </Button>
                    </ModalFooter>
                  </ModalContent>
                </Modal>
                <Modal isOpen={isThreatReportNameModalOpen} onOpenChange={setIsThreatReportNameModalOpen} isDismissable onClose={() => {
                      setIsThreatReportNameModalOpen(false);
                      setThreatReportName('');
                    }} data-sentry-element="Modal" data-sentry-source-file="main.tsx">
                  <ModalContent className='bg-gray-900 border-2 border-gray-200 rounded-lg text-white' data-sentry-element="ModalContent" data-sentry-source-file="main.tsx">
                    <ModalHeader className='bg-gray-800 px-4' data-sentry-element="ModalHeader" data-sentry-source-file="main.tsx">
                      Create New Threat Report
                    </ModalHeader>
                    <ModalBody className='bg-gray-700 px-4 py-4' data-sentry-element="ModalBody" data-sentry-source-file="main.tsx">
                      <Input label="Threat Report Name" labelPlacement="outside" placeholder="Enter threat report name" value={threatReportName} onChange={e => setThreatReportName(e.target.value)} onKeyDown={e => {
                            if (e.key === 'Enter' && threatReportName.trim()) {
                              handleCreateThreatReportWithName();
                            }
                          }} autoFocus classNames={{
                            base: 'w-full',
                            inputWrapper: 'w-90'
                          }} data-sentry-element="Input" data-sentry-source-file="main.tsx" />
                      {threatReportSources.fiveCastFiles.length > 0 && <Select styles={SelectStyles} className="react-select-container font-inter z-[9999] w-full custom-scrollbar2" value={selectedFivecastSource ? {
                            value: selectedFivecastSource,
                            label: selectedFivecastSource
                          } : null} onChange={newValue => {
                            setSelectedFivecastSource((newValue as SingleValue<{
                              value: string;
                              label: string;
                            }>)?.value);
                          }} options={threatReportSources.fiveCastFiles.map((file: any) => ({
                            value: file,
                            label: (file.split('/').slice(-1)[0] || '') === '' ? file.split('/').slice(-2).join('/') : file.split('/').slice(-1)[0]
                          }))} placeholder='Select a Fivecast source' />}
                      {threatReportSources.magnetFiles.length > 0 && <Select styles={SelectStyles} className="react-select-container font-inter z-[9998] w-full custom-scrollbar2" value={selectedMagnetSource ? {
                            value: selectedMagnetSource,
                            label: selectedMagnetSource
                          } : null} onChange={newValue => {
                            setSelectedMagnetSource((newValue as SingleValue<{
                              value: string;
                              label: string;
                            }>)?.value);
                          }} options={threatReportSources.magnetFiles.map((file: any) => ({
                            value: file,
                            label: (file.split('/').slice(-1)[0] || '') === '' ? file.split('/').slice(-2).join('/') : file.split('/').slice(-1)[0]
                          }))} placeholder='Select a Magnet source' />}
                    </ModalBody>
                    <ModalFooter className='bg-gray-800 px-4' data-sentry-element="ModalFooter" data-sentry-source-file="main.tsx">
                      <Button className='bg-gray-300 hover:bg-gray-300 border-gray-600 h-[30px]' onPress={() => {
                            setIsThreatReportNameModalOpen(false);
                            setThreatReportName('');
                          }} data-sentry-element="Button" data-sentry-source-file="main.tsx">
                        Cancel
                      </Button>
                      <Button className='bg-blue-600 hover:bg-blue-700 text-white h-[30px] disabled:opacity-50 disabled:cursor-not-allowed' onPress={handleCreateThreatReportWithName} isDisabled={!threatReportName || threatReportName.trim() === '' || !selectedFivecastSource && !selectedMagnetSource} data-sentry-element="Button" data-sentry-source-file="main.tsx">
                        Create
                      </Button>
                    </ModalFooter>
                  </ModalContent>
                </Modal>
                {featureFlags?.bannerModal && <Modal className="z-[9999999]" isOpen={isBannerModalOpen && !bannerModalAcceptedRef.current} onOpenChange={setIsBannerModalOpen} isDismissable={false} isKeyboardDismissDisabled={true} shadow="lg" backdrop="blur" size="xl" hideCloseButton={true} onClose={() => {
                      setIsBannerModalOpen(false);
                    }}>
                    <ModalContent className='bg-gray-900 border-2 border-gray-200 rounded-lg text-white'>
                      <ModalHeader className='bg-gray-800 px-4'>
                        THIS IS A RESTRICTED INFORMATION SYSTEM FOR USE BY AUTHORIZED USERS ONLY
                      </ModalHeader>
                      <ModalBody className='bg-gray-700 px-4 py-4'>
                        <div className='text-white text-sm'>
                          <p>By accessing this software and data you are acknowledging that the use of the information from {tenantName && tenantName.trim() !== '' ? tenantName : !tenantId && orgName && orgName.trim() !== '' ? orgName : 'your organization'} shall only be used by criminal justice officials acting in their official capacities for criminal justice purposes.</p>
                          <p>Any other non-criminal justice use must be authorized by law.</p>
                          <p>Users of this system have no explicit or implicit expectation of privacy.</p>
                          <p>Any and all users of this system and all files contained herein may be intercepted, monitored, recorded, copied, audited, inspected and disclosed to authorized law enforcement personnel in accordance with the FBI CJIS Security Policy.</p>
                          <p>Misuse of the information in this system may be a misdemeanor or felony and may result in civil penalties as well.</p>
                          <p>By clicking &quot;I Accept&quot;, I acknowledge, understand, and agree to comply with these statements.</p>
                        </div>
                      </ModalBody>
                      <ModalFooter className='bg-gray-800 px-4'>
                        <Button className='bg-blue-600 hover:bg-blue-700 text-white h-[30px]' onPress={() => {
                            setIsBannerModalOpen(false);
                            bannerModalAcceptedRef.current = true;
                          }}>
                          I Accept
                        </Button>
                        <Button className='bg-gray-300 hover:bg-gray-300 border-gray-600 h-[30px]' onPress={() => {
                            handleLogout();
                          }}>
                          Cancel
                        </Button>
                      </ModalFooter>
                    </ModalContent>
                  </Modal>}
                {feedback && !featureFlags?.useIntercom && <div ref={feedbackButtonRef} className={showFeedbackButton ? `flex-row items-center justify-left overflow-hidden transition-all duration-700 ease w-5
                      hover:w-24 pr-3 hover:pr-4 pl-3 hover:pl-4 right-[20px] bottom-[20px] py-3 cursor-pointer
                      fixed bg-white rounded-full h-[20px] text-black z-[20000]` : `fixed w-1 h-1 opacity-100 b-[0px] r-[0px]`}>
                    {showFeedbackButton && <button type="button" className="flex flex-row items-center justify-left z-[20000]">
                        <MegaphoneIcon mirrored className="text-black ml-[-2px] mt-[-1px]" size={24} />
                        <div className={`transition-width duration-3000 ease pl-[10px] pr-2 w-18 mt-[-2px]`}>Feedback</div>
                      </button>}
                  </div>}
              </FullScreenContext.Provider>
            </FeaturesContext.Provider>
          </ToastContext.Provider>
          </ToastProvider>
        </div>
      </WalkthruContext.Provider>
      </AuthContext.Provider>
      </CSMProvider>
    </StatsContext.Provider>;
};
export default Main;